import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isPayLockRefusal, payAlreadyPaidMessage } from '../../lib/pay-lock';
import { v4 as uuidv4 } from 'uuid';
import Header from '../../components/Header';
import { api } from '../../api/client';
import { unwrap, unwrapArray } from '../../lib/api-envelope';
import RateSheetItemPicker, { type RateSheetItemsState } from '../../components/tickets/RateSheetItemPicker';
import { applyRateSheetItem, type RateSheetItem } from '../../lib/rate-sheet-items';
import { reindexAfterRemoval } from '../../lib/line-item-rows';
import { RowError, rowErrorRingClass, groupRowErrors, type RowErrorReason } from '../../components/ui/RowError';
import {
  validateLineItems as checkLineItems,
  bannerForRowErrors,
  splitRowReasons,
  fieldErrorClass,
  clearFieldError,
  scrollRowIntoView,
} from '../../lib/line-item-validation';
import { getLineItemTypeLabel, toLineItemType } from '../../constants/line-item-types';
import { visibleLineItemTypeOptions } from '../../lib/line-item-prefs';
import { useHiddenLineItemTypes } from '../../hooks/useLineItemPrefs';
import { UNIT_OPTIONS, normalizeUnit } from '../../constants/units';
import { useAuth } from '../../contexts/AuthContext';
import { useOfflineQuery } from '../../lib/offline/use-offline-query';
import { useOfflineMutation } from '../../lib/offline/use-offline-query';
import {
  buildDiscountLine, discountFieldsFor, editDiscountLine, fmtLineMoney, isDiscountLine, lineForWire,
  isDiscountEstimate, queuedDiscountRefusals, readDiscountFields, stampDiscountBase, withDiscountEstimates, withoutDiscountEstimates,
  type DiscountEdit, type DiscountFields, type DiscountSpec,
} from '../../lib/ticket-discount';
import { getRejectedTicketSave } from '../../lib/offline/sync-queue';
import { AddDiscountButton, DiscountLineRow } from '../../components/tickets/DiscountControls';
import { useSyncStore } from '../../lib/offline/sync-store';
import { invalidateTickets } from '../../lib/tickets-invalidation';
import { formatTicketHours, hasContradictoryTimes, ticketTimeKey } from '../../lib/ticket-hours';
import {
  FIELD_TICKET_TABS,
  fieldTicketTabFilter,
  fieldTicketStatusLabel,
  ticketStatusGroup,
  normalizeTicketStatus,
} from '../../lib/ticket-status';
import TicketHoursReadout from '../../components/tickets/TicketHoursReadout';
import EquipmentHoursRuns from '../../components/tickets/EquipmentHoursRuns';
import LastEditedStamp from '../../components/LastEditedStamp';
import DatePicker from '../../components/DatePicker';
import TimePicker from '../../components/TimePicker';
import VoiceRecorder from '../../components/VoiceRecorder';
import PhotoUploader from '../../components/PhotoUploader';
import { blurOnWheel } from '../../lib/number-input';
import {
  equipmentUsageError,
  hydrateEquipmentUsageRow,
  seedEquipmentUsageRow,
  serializeEquipmentUsage,
  stepDays,
  type EquipmentUsageRow,
} from '../../lib/equipment-usage';
import { describeAiDraft, readRateSheetResolution, type AiDraftNotice } from '../../lib/rate-sheet-resolution';
import { getJobTypeLabel } from '../../constants/job-types';
import { calendarDayString, formatCalendarDate, todayCalendarDate } from '../../lib/calendar-date';
import { readTicketDate } from '../../lib/ticket-date';

import { assignTechSlot, crewId, crewLabel, findCrewById } from '../../lib/crew-slot';
import { clearTechSlots, prefillTechSlots } from '../../lib/ticket-slot-prefill';
import { allPickable, buildCrewPickerLists, orphanSlotOption, pickerListsForSlot } from '../../lib/crew-picker';
import { readCachedJobCrew } from '../../lib/offline/cached-job-crew';
import { readCachedJobCasingStrings, cacheJobCasingStrings } from '../../lib/offline/casing-string-cache';
import CasingStringMetresFields from '../../components/casing/CasingStringMetresFields';
import {
  buildCasingStringMetres, draftFromRecorded, readRecordedMetres, stringsThatTakeMetres, totalIsStringSum,
  type MetresDraft,
} from '../../lib/ticket-string-metres';
import type { JobCasingString } from '../../types/casing-string';
import { describeAiReplace } from '../../lib/ai-suggest-replace';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { fieldTicketWriteRefused, ticketWriteRefusalReason } from '../../lib/field-ticket-write';
import FieldTicketDisplay from '../../components/tickets/FieldTicketDisplay';
import { fmtCurrency, fmtQty, sumMoney } from '../../lib/display-labels';
import { carryExistingLineIds, readPrefillDraftId } from '../../lib/ai-draft-lines';
import {
  readReviewTotals,
  readSavedTicketRow,
  reviewLineItemsFrom,
  type FieldTicketReviewTotals,
  type ReviewLineItem,
  readSavedLineItems,
} from '../../lib/field-ticket-review';
import {
  ArrowLeft,
  Send,
  Edit3,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertTriangle,
  Info,
  Trash2,
  Calendar,
  MapPin,
  FileText,
  Sparkles,
  Loader,
  DollarSign,
  Wrench,
  Plus,
  ClipboardCheck,
  WifiOff,
} from 'lucide-react';
import { useBack } from '../../hooks/useBack';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { Tooltip } from '../../components/ui/tooltip';
import { priceBookLinkState, readDraftRateSheetItemId, readOffBook } from '../../lib/price-book-link';


/** The stored day as 'YYYY-MM-DD'; an unreadable value passes through as it came, absent as ''. */
const calendarDayOrRaw = (v: string | null | undefined): string => calendarDayString(v) ?? (v ?? '');

// ── Casing billing types, mirrored from NewTicketScreen.tsx (ops editor) ──
// Duplicated, not shared: the ops editor renders this UI inline (no shared
// component), and the field screen is offline-aware with its own state model.
interface LineItem extends DiscountFields {
  /**
   * The server's row id, or null for a row this hand has just added.
   *
   * THE FIELD EDITOR USED TO DROP THIS. It rebuilt each row from the server's
   * shape and carried no id, which sync.routes.ts states in its own words —
   * "the queued payload has no ids to upsert on: the field editor rebuilds each
   * row from the server's shape and does not carry `id` through". That is the
   * gap this closes, and it is why the save could only ever mean "replace the
   * ticket's whole line-item set": with no ids there was nothing else it COULD
   * mean.
   *
   * With ids, plus the `knownLineItemIds` baseline below, a save says which
   * rows it is speaking for — so another hand's rows, which this device was
   * never handed, are left alone instead of being deleted by omission.
   */
  id: string | null;
  lineItemType: string;
  description: string;
  unit: string | null;
  rate: number | null;
  quantity: number | null;
  total: number | null;
  revenueCode: string | null;
  /**
   * The rate-sheet row the picker priced this line from (API 0406), or null
   * for a line typed by hand. It decides the QuickBooks item, so it must ride
   * along on every save — see lib/rate-sheet-items.ts, note 3. Editing the
   * description or rate never clears it; only re-picking a row replaces it.
   */
  rateSheetItemId: string | null;
  /**
   * 0504: the office's "deliberately not a price-book item". `undefined` is
   * "say nothing" and is never sent — see lib/price-book-link.ts for why a
   * missing column must not become `false`.
   */
  offBook?: boolean;
  assignedTech1Initials: string | null;
  assignedTech2Initials: string | null;
  assignedTech3Initials: string | null;
  assignedTech4Initials: string | null;
  // Authoritative slot; the initials above stay for display. See lib/crew-slot.ts.
  assignedTech1Id: string | null;
  assignedTech2Id: string | null;
  assignedTech3Id: string | null;
  assignedTech4Id: string | null;
}

// EquipmentUsageRow lives in lib/equipment-usage.ts, with the hours/runs
// wire rules (api#978, #987) shared with the office editor.

/**
 * What the SERVER has said about this ticket's equipment usage — three states,
 * not two, and the third one is the whole correctness of the prefill below.
 *
 *   'unloaded' — the payload carried no `equipmentUsage` key at all. An older
 *                API, or a ticket read from the offline cache. We do not know
 *                whether rows exist, so we must not act.
 *   'empty'    — the server answered, with none. Nobody has recorded usage on
 *                this ticket yet, so seeding it from the job is new information.
 *   'rows'     — the server answered, with rows. A hand has already said what
 *                was used, INCLUDING when every entry is wasUsed:false. Their
 *                answer is the answer; seeding over it would flip units back on
 *                every time they reopened the ticket.
 *
 * Collapsing 'unloaded' into 'empty' is the bug this type exists to prevent:
 * they are the same `[]` on screen and opposite instructions to the prefill.
 */
type SavedUsageState = 'unloaded' | 'empty' | 'rows';

// crewInitials / crewLabel now live in lib/crew-slot.ts, shared with
// NewTicketScreen — two copies of a display rule is how they drift.

// Signature of the four date/time columns — used to tell whether the server's
// total_hours still describes what's on screen.
function timeKeyOf(t: any): string {
  return ticketTimeKey(t?.work_date ?? t?.date, t?.end_date, t?.start_time, t?.end_time);
}

/**
 * Server row shape → the editor's row shape.
 *
 * ONE copy, because it now runs twice: when a ticket is opened, and again from
 * the save response so a second save is not made against the first one's
 * baseline. Two copies of a mapping that must agree about `id` is how the
 * baseline and the rows it describes come apart.
 */
function hydrateLineItems(rawLines: readonly any[]): LineItem[] {
  // Stamped with the base the server priced any Multi Rig line from, so its
  // total is the server's until a base line changes — lib/ticket-discount.
  return stampDiscountBase(rawLines.map((li: any) => ({
      id: li.id != null ? String(li.id) : null,
      lineItemType: toLineItemType(li.line_item_type || li.lineItemType) ?? 'other',
      description: li.description ?? '',
      unit: li.unit || null,
      rate: li.rate != null ? Number(li.rate) : null,
      quantity: li.quantity != null ? Number(li.quantity) : null,
      total: li.total != null ? Number(li.total) : null,
      revenueCode: li.revenue_code || li.revenueCode || null,
      rateSheetItemId: li.rate_sheet_item_id || li.rateSheetItemId || null,
      offBook: readOffBook(li),
      assignedTech1Initials: li.assigned_tech_1_initials || li.assignedTech1Initials || null,
      assignedTech2Initials: li.assigned_tech_2_initials || li.assignedTech2Initials || null,
      assignedTech3Initials: li.assigned_tech_3_initials || li.assignedTech3Initials || null,
      assignedTech4Initials: li.assigned_tech_4_initials || li.assignedTech4Initials || null,
      assignedTech1Id: li.assigned_tech_1_id || li.assignedTech1Id || null,
      assignedTech2Id: li.assigned_tech_2_id || li.assignedTech2Id || null,
      assignedTech3Id: li.assigned_tech_3_id || li.assignedTech3Id || null,
      assignedTech4Id: li.assigned_tech_4_id || li.assignedTech4Id || null,
      ...readDiscountFields(li),
  })));
}

export default function FieldTicketsScreen() {
  const goBack = useBack('/field');
  const location = useLocation();
  const queryClient = useQueryClient();
  const { company, user } = useAuth();
  const timeZone = useTenantTimeZone();
  const currency = company?.country === 'CA' ? 'CAD' : 'USD';
  const initialTab = (location.state as any)?.tab || 'open';
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [editingTicket, setEditingTicket] = useState<any>(null);
  // Just the id. The poll option above must not change identity on every
  // keystroke, and `editingTicket` does.
  const editingTicketId: string | null = editingTicket?.id ?? null;
  const [message, setMessage] = useState<{ type: 'success' | 'warning' | 'error'; text: string; sticky?: boolean } | null>(null);
  // Every auto-dismiss goes through this. A STICKY message (the pay-approved
  // date lock) is left alone, so a timer armed by an earlier banner — a 3s
  // "saved" from a moment before — cannot clear a refusal the hand still has
  // to act on. Any new message still replaces it.
  const clearTransientMessage = () => setMessage(m => (m?.sticky ? m : null));
  /*
   * A sticky message is a refusal or a warning the hand has to read (api#1352:
   * the approved-pay lock and the paid-pay warning). The banner is at the top
   * of the editor and Save Draft is at the bottom. Measured on Smith at 390px:
   * the refusal arrived with the banner off-screen, and the save looked like it
   * had done nothing. So a sticky message is scrolled to as it appears. Save
   * Draft closes the editor, so its message is on the LIST banner, and the list
   * keeps the scroll the editor had; that one is scrolled to the same way.
   */
  const editorMessageRef = useRef<HTMLDivElement | null>(null);
  const listMessageRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (message?.sticky) scrollRowIntoView(editorMessageRef.current ?? listMessageRef.current);
  }, [message]);

  // Casing billing state — populated from the loaded ticket and/or AI Suggest.
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  /**
   * The rows the server handed this device, or null when we were never handed
   * any (an offline create, or a cached payload from a bundle that predates
   * this).
   *
   * NULL AND [] ARE OPPOSITE INSTRUCTIONS and the API reads them that way: the
   * key's absence means "replace the whole set", `[]` means "I hold nothing, so
   * I can add but not delete". Sending `?? []` here would quietly make a line
   * undeletable from a device that had not been handed one.
   */
  const [knownLineItemIds, setKnownLineItemIds] = useState<string[] | null>(null);
  /**
   * The AI prefill draft this editing session started from (API 0421), sent
   * back on save so the API can link the edit to what the model proposed.
   * Null until AI Suggest succeeds, and reset whenever a ticket is opened or
   * closed — a save the hand made without AI must not cite a draft.
   */
  const [aiDraftId, setAiDraftId] = useState<string | null>(null);
  // A list per row, not a string: the API can refuse one row for more than one
  // reason at a time, and a hand in a dead zone who fixes the rate and meets the
  // same 400 about the quantity has no way to tell it apart from a failed save.
  const [lineItemErrors, setLineItemErrors] = useState<Record<number, RowErrorReason[]>>({});
  const [lineItemTypeErrors, setLineItemTypeErrors] = useState<Record<number, string>>({});
  /*
   * The rendered row per index, so a refusal can bring the offending line on
   * screen. A ticket runs to five or more lines on a phone and the marked one
   * is routinely below the fold, which turns a precise refusal back into
   * "something failed" — the exact outcome the row marks exist to end.
   *
   * A ref rather than an id lookup because two editors render rows with the
   * same shape and a document-wide query would find whichever mounted last.
   */
  const lineItemRowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // The types this company hides from the picker. A row already typed as a
  // hidden value keeps its own option — see visibleLineItemTypeOptions.
  const { hidden: hiddenLineItemTypes } = useHiddenLineItemTypes();
  // Set when a picked price book row carried a unit this app cannot store — a
  // period rate like "5 day", which the canonical vocabulary has no way to
  // express. Rendered on the row, because the alternative to saying so is
  // resolving "5 day" to "per day" and billing a fifth of the work.
  const [lineItemUnitNotes, setLineItemUnitNotes] = useState<Record<number, string>>({});
  const [crew, setCrew] = useState<any[]>([]);
  // Which hand-added rows the job-position pre-fill may still suggest crew
  // for (lib/ticket-slot-prefill.ts). Keyed by row index like the unit notes,
  // and reset with them on every wholesale replacement of the rows — so a row
  // that arrived from the server or the AI draft is never in it and never
  // suggested over. 'open': eligible, nothing suggested yet. 'prefilled': the
  // slots hold a suggestion nobody has touched, shown as such. A person
  // picking any slot removes the row for good.
  const [slotPrefill, setSlotPrefill] = useState<Record<number, 'open' | 'prefilled'>>({});
  // AI Suggest is waiting on the hand to agree to lose the lines already here.
  const [aiReplaceConfirm, setAiReplaceConfirm] = useState(false);
  const [equipmentUsage, setEquipmentUsage] = useState<EquipmentUsageRow[]>([]);
  const [savedUsageState, setSavedUsageState] = useState<SavedUsageState>('unloaded');
  const [aiReasoning, setAiReasoning] = useState<string | null>(null);
  const [aiConfidence, setAiConfidence] = useState<string | null>(null);
  // Held apart from `message` because that toast auto-dismisses after 5s. A
  // hand on a lease needs to still see WHY the rates are blank when they get
  // to the line items.
  const [aiRateSheetChoice, setAiRateSheetChoice] = useState<AiDraftNotice['candidates']>([]);
  const [loadedTimeKey, setLoadedTimeKey] = useState<string | null>(null);
  /*
   * ─── The Review step ────────────────────────────────────────────────────
   *
   * Non-null while the hand is looking at the read-only summary, and it is the
   * SAVE RESPONSE, not a copy of the editor's state. A hand cannot be shown
   * "what the office will see" from the device's own arithmetic; the only
   * honest source is the row the server just committed.
   *
   * Setting this switches the third view on. It is cleared on Back/Edit, on a
   * successful submit, and whenever the open ticket changes — a stale review of
   * a ticket that is no longer open would render another ticket's money under
   * this one's header.
   */
  const [review, setReview] = useState<{
    totals: FieldTicketReviewTotals;
    lineItems: ReviewLineItem[];
  } | null>(null);
  // The submit confirmation. Submit fires from here and nowhere else.
  const [submitConfirm, setSubmitConfirm] = useState(false);
  // The ticket id whose edit form the hand has explicitly asked for, after being
  // told what asking does. Only office_approved consults it — see isEditable.
  // An id rather than a boolean so a different ticket is never silently
  // unlocked; reset below when the open ticket changes, so closing and
  // reopening asks again.
  const [editUnlockedFor, setEditUnlockedFor] = useState<string | null>(null);
  const [editUnlockConfirm, setEditUnlockConfirm] = useState(false);

  // Keyed on the id, so a keystroke (which replaces editingTicket with a new
  // object carrying the same id) does NOT relock the form mid-edit, while
  // closing the ticket (id -> undefined) does.
  useEffect(() => {
    setEditUnlockedFor(null);
    setEditUnlockConfirm(false);
  }, [editingTicket?.id]);

  // A sticky message is about the ticket it was raised on. Opening a ticket
  // retires it: "Pay on this ticket is already paid" over a DIFFERENT ticket's
  // editor would be a false statement about that one. Closing (id -> undefined)
  // keeps it, because Save Draft closes the editor and its warning moves to
  // the list banner.
  useEffect(() => {
    if (editingTicket?.id) setMessage(m => (m?.sticky ? null : m));
  }, [editingTicket?.id]);

  // All or nothing: one line whose total is not there makes the subtotal an
  // em dash, not the sum of the others. See NewTicketScreen's twin.
  // The lines as shown: a Multi Rig line whose base lines changed on this screen
  // carries the editor's estimate (lib/ticket-discount); every other line is
  // exactly what is in state.
  const shownLines = useMemo(() => withDiscountEstimates(lineItems), [lineItems]);
  const subtotal = sumMoney(shownLines.map(li => li.total));

  /*
   * A save made offline and refused later (/sync/push) has its reasons put back
   * on the discount line when the ticket is opened again — "if the save is
   * refused later, show the API's message on the discount line". Once per
   * opened ticket, after its lines have arrived; see queuedDiscountRefusals for
   * why a reason that no longer matches its line is dropped rather than placed.
   */
  const shownLinesRef = useRef(shownLines);
  shownLinesRef.current = shownLines;
  const refusalCheckedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!editingTicketId) { refusalCheckedFor.current = null; return; }
    if (refusalCheckedFor.current === editingTicketId || shownLines.length === 0) return;
    refusalCheckedFor.current = editingTicketId;
    const forTicket = editingTicketId;
    getRejectedTicketSave(forTicket)
      .then(rejected => {
        if (!rejected || refusalCheckedFor.current !== forTicket) return;
        const placed = queuedDiscountRefusals(rejected.lineItems, rejected.details, shownLinesRef.current);
        if (Object.keys(placed).length > 0) setLineItemErrors(prev => ({ ...placed, ...prev }));
      })
      // The queue being unreadable is the sync bar's to report, not this row's.
      .catch(() => {});
  }, [editingTicketId, shownLines.length]);

  // Handle create-from-job flow
  const createFromJob = (location.state as any)?.createFromJob;
  const createJobNumber = (location.state as any)?.jobNumber;

  /*
   * ─── Arriving here to OPEN a ticket rather than to make one ──────────────
   *
   * The job screen now lists a job's existing tickets, and tapping one has to
   * land in this editor. Router state, matching the `createFromJob` and `tab`
   * conventions already on this screen, rather than a new /field/tickets/:id
   * route — the editor is not a route, it is `editingTicket` state on this
   * component, and inventing a route for it would mean a page a refresh cannot
   * render and a nav entry that cannot exist.
   *
   * READ ONCE INTO A REF-LIKE STATE. The effect below has to wait for the list
   * to arrive, so it cannot clear router state on its first run the way the
   * create effect does; and it must not re-open the ticket after the hand
   * closes it. `pendingOpenId` is consumed exactly once and then nulled.
   */
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(
    () => (location.state as any)?.openTicketId ?? null,
  );

  // Tab → status mapping lives in src/lib/ticket-status.ts so the badge below
  // and these filters can never drift apart. Fetch the full list once and filter
  // in the client: the tab keys are not API status values, and passing them as
  // `?status=` is what silently emptied the Submitted and Approved tabs.
  const { data, isLoading, refetch } = useOfflineQuery(
    'tickets',
    ['field-my-tickets'],
    () => api.employee.myTickets().then(r => r.data.data ?? r.data ?? []),
    // Poll ONLY while a ticket is open. See the LastEditedStamp below: its
    // whole job is to say the row changed under you, and it cannot do that from
    // a list that stops moving the moment you start typing.
    { refetchInterval: editingTicketId ? 60_000 : false },
  );

  const allTickets: any[] = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const tickets: any[] = useMemo(() => {
    const filterFn = fieldTicketTabFilter(activeTab);
    return filterFn ? allTickets.filter(filterFn) : allTickets;
  }, [allTickets, activeTab]);

  const isOnline = useSyncStore((s) => s.isOnline);

  /**
   * The open ticket AS THE SERVER CURRENTLY HAS IT — not the copy this editor
   * opened with.
   *
   * `editingTicket` is lifted out of the list into component state and the
   * hydration effect is keyed on its id, so once the editor is open nothing
   * refreshes it. The "edited by Rene, 14 min ago" stamp read that frozen copy,
   * which means it reported who had touched the ticket BEFORE you opened it and
   * never moved again — inert during exactly the window it exists to cover.
   *
   * That stamp is, in its own words, "the only thing that makes such an
   * overwrite visible to a human". A frozen one is worse than none: it looks
   * like a live signal.
   *
   * Deliberately only the STAMP reads this. The hand's in-progress edits stay
   * in `editingTicket` and `lineItems` and are never overwritten by the poll —
   * this is a notice that the row moved, not a reload. Growing it into conflict
   * detection or a lock is a separate design decision, and the component says
   * so.
   */
  const liveEditingRow = useMemo(
    () => (editingTicketId ? allTickets.find((t: any) => t?.id === editingTicketId) ?? null : null),
    [allTickets, editingTicketId],
  );

  // ─── The job behind the ticket being edited ───────────────────────────────
  //
  // One fetch, two consumers: the job number for the header (`tickets` does not
  // carry it) and `jobs.rate_sheet_id`, which orders the price book picker's
  // groups. It replaces an imperative `.then()` fetch that ran on ticket-open
  // whenever the job number was missing — data fetching here is React Query, and
  // a second raw call for the same row would be a second answer to cache.
  //
  // It does now run on every ticket-open rather than only when the number is
  // absent, because the rate sheet id is wanted either way. Cached per job for
  // five minutes, so opening several tickets on one job costs one request.
  //
  // Offline it never runs, and the header falls back to the job id's first eight
  // characters exactly as it did before.
  const editingJobId: string | null = editingTicket?.job_id ?? null;
  const { data: editingJob } = useQuery<any>({
    queryKey: ['field-job-for-ticket', editingJobId],
    queryFn: () => api.employee.myJob(editingJobId!).then(r => unwrap<any>(r.data)),
    enabled: !!editingJobId && isOnline,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const editingJobNumber: string | null =
    editingJob?.job_number ?? editingJob?.jobNumber ?? null;

  // ─── Who the Tech 1-4 picker can name ─────────────────────────────────────
  //
  // The ticket arrives carrying its job's crew (API #518 attaches it to every
  // field ticket GET), and the hydrate below reads it. That is the first
  // source. When a ticket carries none — created offline, or cached before
  // #518 — the job's own record answers: the live one when there is signal,
  // the IndexedDB copy when there is not. See lib/offline/cached-job-crew.ts
  // for what the cache can and cannot say.
  useEffect(() => {
    if (!editingTicket || crew.length > 0) return;
    const jobCrew: any[] = Array.isArray(editingJob?.crew) ? editingJob.crew : [];
    if (jobCrew.length > 0) {
      setCrew(jobCrew);
      return;
    }
    const jobId: string | null = editingTicket.job_id ?? editingTicket.jobId ?? null;
    if (!jobId) return;
    let cancelled = false;
    readCachedJobCrew(jobId).then(found => {
      if (!cancelled && found.length > 0) setCrew(found);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTicket?.id, editingJob, crew.length]);

  // The job crew is the default list, not a closed one: a hand picked up
  // mid-shift is on this ticket before anyone has put them on the roster.
  // Everyone else at the company goes under a second heading — but only once
  // the hand asks for it. Off on every open: TNT has 49 field workers, and a
  // hand looking for three names should not scroll a roster. The switch is
  // what turns on the fetch. Online only — IndexedDB has no users store — so
  // in a dead zone the picker is the crew and the switch is not offered.
  const [showEveryone, setShowEveryone] = useState(false);
  useEffect(() => { setShowEveryone(false); }, [editingTicket?.id]);

  // ─── Metres run per casing string (api#830) ───────────────────────────────
  //
  // The job's strings come from the live job read when there is signal, and
  // from the device's copy of the job when there is not (the sync pull folds
  // them on — lib/offline/casing-string-cache). `null` is "not known": no
  // per-string inputs, and the ticket's single total input stays as it was.
  const [ticketStrings, setTicketStrings] = useState<JobCasingString[] | null>(null);
  useEffect(() => {
    const jobId: string | null = editingTicket?.job_id ?? editingTicket?.jobId ?? null;
    if (!jobId) { setTicketStrings(null); return; }
    if (Array.isArray(editingJob?.casingStrings)) {
      setTicketStrings(editingJob.casingStrings);
      cacheJobCasingStrings(jobId, editingJob.casingStrings);
      return;
    }
    let cancelled = false;
    readCachedJobCasingStrings(jobId).then(found => { if (!cancelled) setTicketStrings(found); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTicket?.id, editingJob]);

  // What the ticket has recorded. `null` when the row carries no
  // casingStringMetres key — see the sentinel note in lib/ticket-string-metres.
  const recordedMetres = readRecordedMetres(editingTicket);
  const [metresDraft, setMetresDraft] = useState<MetresDraft>({});
  useEffect(() => {
    setMetresDraft(draftFromRecorded(readRecordedMetres(editingTicket)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTicket?.id]);
  const hasStringInputs = stringsThatTakeMetres(ticketStrings ?? []).length > 0;

  /*
   * A review belongs to the ticket it was saved from. Closing the editor or
   * opening a different ticket drops it — otherwise the third view would come
   * up holding the previous ticket's committed totals under the new ticket's
   * header, which is the one failure a review step must not have.
   */
  useEffect(() => {
    setReview(null);
    setSubmitConfirm(false);
  }, [editingTicket?.id]);
  const { data: companyUsers } = useQuery<any[]>({
    queryKey: ['crew-picker-users'],
    queryFn: () => api.users.list().then(r => unwrapArray<any>(r.data)),
    enabled: isOnline && !!editingTicket && showEveryone,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const crewLists = useMemo(() => buildCrewPickerLists(crew, companyUsers), [crew, companyUsers]);

  // ─── The price book behind the Description field ──────────────────────────
  //
  // Same endpoint and same scoping as the ops editor, deliberately: these two
  // screens are copies of each other and a second source is how they drift. See
  // the note there, and src/lib/rate-sheet-items.ts for why the job's own rate
  // sheet sorts rather than filters.
  //
  // ONLINE ONLY, for now. IndexedDB has no store for rate sheet items (v3 holds
  // tickets, ticketLineItems, jobs, equipment and customers), so there is
  // nothing to read in a dead zone. The picker says so and the field stays a
  // plain text input — a write must never degrade to silence, and a hand who
  // cannot see why the menu is empty reads the screen as broken.
  const ticketCustomerId: string | undefined =
    editingTicket?.customer_id ?? editingTicket?.customerId ?? undefined;
  const jobRateSheetId: string | null =
    editingJob?.rate_sheet_id ?? editingJob?.rateSheetId ?? null;

  const {
    data: rateSheetItems,
    isLoading: rateItemsLoading,
    isError: rateItemsFailed,
  } = useQuery<RateSheetItem[]>({
    queryKey: ['rate-sheet-items', ticketCustomerId ?? null],
    queryFn: () =>
      api.rateSheets.items({ customer_id: ticketCustomerId }).then(r => unwrapArray<RateSheetItem>(r.data)),
    enabled: isOnline && !!ticketCustomerId && !!editingTicket,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const rateItemsState: RateSheetItemsState =
    !isOnline ? 'offline'
    : !ticketCustomerId ? 'no-customer'
    : rateItemsFailed ? 'failed'
    : rateItemsLoading ? 'loading'
    : (rateSheetItems?.length ?? 0) === 0 ? 'empty'
    : 'ready';

  const createMutation = useOfflineMutation(
    'tickets',
    'create',
    (vars: any) => api.employee.createTicket({ jobId: vars.data.job_id }),
    {
      invalidateKeys: [['field-my-tickets']],
      onSuccess: (res: any) => {
        const ticket = res?.data?.data ?? res?.data ?? res;
        invalidateTickets(queryClient);
        setMessage({ type: 'success', text: isOnline ? 'Ticket created as draft' : 'Ticket saved offline — will sync when back online' });
        if (ticket?.id) setEditingTicket(ticket);
        setTimeout(clearTransientMessage, 3000);
      },
      // Create had no error handler at all, so an offline create that could
      // not be queued showed neither a success nor a failure — the user tapped
      // and nothing happened. Wrapped rather than passed by reference because
      // handleMutationError is declared below this call.
      onError: (err: any) => handleMutationError(err),
    }
  );

  const handleMutationError = (err: any) => {
    const resData = err?.response?.data;
    // Only when the refusal names rows. A 400 with an empty details[] (the
    // discount door's "this save would remove every line…") marks nothing, so a
    // banner pointing at marked lines would point at none; its own sentence
    // is shown instead, by the branch below.
    if (err?.response?.status === 400 && Array.isArray(resData?.details) && resData.details.length > 0) {
      const grouped = groupRowErrors(resData.details);
      setLineItemErrors(grouped);
      /*
       * Built from the rows, not read off `resData.error` — see the same note
       * in NewTicketScreen. The API sends the constant "Invalid line items",
       * which names no kind and does not point at the marked rows.
       */
      setMessage({ type: 'error', text: bannerForRowErrors(grouped) });
      // Same treatment a local refusal gets, for the same reason: the marked
      // row is usually below the fold on a phone.
      const first = Object.keys(grouped).map(Number).sort((a, b) => a - b)[0];
      if (first != null) scrollRowIntoView(lineItemRowRefs.current[first]);
      /*
       * NO AUTO-DISMISS ON A REFUSAL, and the return is what enforces it.
       *
       * A refusal is not a notification: it is a piece of work the hand still
       * owes, and it stays up until the save is tried again — which is exactly
       * what the local refusal does (`validateLineItems` sets the message and
       * arms no timer). The five-second clear below applied to both branches,
       * so the same blank rate got a banner that persisted when the device
       * caught it and one that evaporated when the server did. On a phone
       * showing two of five rows, five seconds can elapse while scrolling to
       * the row the banner was about.
       *
       * The rows keep their marks either way, so this was never a silent
       * failure — but the reader who looks up after scrolling finds the
       * explanation gone, and nothing says a save was refused at all.
       */
      return;
    }
    // `err.message` carries the offline-storage failure text from
    // useOfflineMutation — "Failed to save ticket" would imply a retry could
    // work, which it cannot until the device is back on connection.
    // Either approved-pay lock: dates (api#1321) or pay-affecting lines
    // (api#1352). Its `error` names the remedy, including "reopen the pay
    // period" when the period is approved, so it is shown verbatim.
    const payLocked = isPayLockRefusal(resData);
    setMessage({ type: 'error', text: resData?.error ?? err?.message ?? 'Failed to save ticket', ...(payLocked ? { sticky: true } : {}) });
    // A change refused because crew pay on this ticket is already approved is
    // a refusal the hand has to act on (ask the office to unapprove), not a
    // notification. So, like the row refusal above, it stays up until the
    // next save attempt replaces it.
    if (payLocked) return;
    // Still transient, deliberately: this half names a condition the hand
    // cannot act on line by line, and it has no row mark to leave behind.
    setTimeout(clearTransientMessage, 5000);
  };

  const updateMutation = useOfflineMutation(
    'tickets',
    'update',
    (vars: any) => api.employee.updateTicket(vars.id, vars.data),
    {
      invalidateKeys: [['field-my-tickets']],
      /*
       * ONE save path, two outcomes. Save Draft closes the editor; Review keeps
       * it open and shows what was committed.
       *
       * `vars.review` rather than a second useOfflineMutation('tickets',
       * 'update', …) because the two would be the same write through the same
       * endpoint with the same offline queue semantics, and a second one is a
       * second place for the queueing, the invalidation and the 400-with-rows
       * handler to drift. The flag rides on the mutation vars, which the hook
       * hands back to onSuccess untouched (the mutationFn reads only
       * id/version/data).
       */
      onSuccess: (res: any, vars: any) => {
        invalidateTickets(queryClient);
        // Saved on pay that is already exported or paid (api#1352): the API's
        // sentence, sticky, as a warning and not a tick. Offline the response
        // is a local stub with no warnings, which is right: nothing has been
        // decided by the server yet.
        const paidWarning = payAlreadyPaidMessage(res?.data);
        // Re-seed from what COMMITTED, so a second save is not made against the
        // baseline of the first. The response carries the ticket's live rows
        // with their ids — including rows another hand added, which this device
        // is now holding for the first time. Without this, saving twice with
        // the editor open would send the original baseline again and the second
        // save would be reasoning about rows that have since changed id.
        //
        // Skipped offline, where the response is a local stub with no rows:
        // there the queued mutation already carries the baseline it was built
        // against, which is the right one for it.
        const committed = readSavedLineItems(res);
        if (committed) {
          // Same rule as the open path: a baseline is only claimed when every
          // committed row can be identified. See the note there.
          const ids = committed.map((li: any) => (li?.id != null ? String(li.id) : null));
          setKnownLineItemIds(ids.every((id: string | null) => id) ? (ids as string[]) : null);
          setLineItems(hydrateLineItems(committed));
          // Saved: what was a suggestion is now the record.
          setSlotPrefill({});
        }
        if (vars?.review) {
          // Merge the committed row over the open ticket — null offline, where
          // the response is a local stub. See readSavedTicketRow.
          const saved = readSavedTicketRow(res);
          if (saved) setEditingTicket((prev: any) => (prev ? { ...prev, ...saved } : prev));
          if (paidWarning) setMessage({ type: 'warning', text: paidWarning, sticky: true });
          setReview({
            totals: readReviewTotals(res),
            // Offline the review shows this device's lines, and the customer
            // reads it: a discount the server has not priced shows "—", never
            // the editor's estimate (lib/ticket-discount withoutDiscountEstimates).
            lineItems: reviewLineItemsFrom(res, withoutDiscountEstimates(shownLines)),
          });
          return;
        }
        setEditingTicket(null);
        if (paidWarning) {
          setMessage({ type: 'warning', text: paidWarning, sticky: true });
          return;
        }
        setMessage({ type: 'success', text: isOnline ? 'Ticket updated' : 'Changes saved offline — will sync when back online' });
        setTimeout(clearTransientMessage, 3000);
      },
      onError: handleMutationError,
    }
  );

  // Save Draft and Review are the same write, told apart by `review` on the
  // vars. React Query keeps the in-flight vars on the mutation, so the two
  // buttons read their own pending state off it rather than off a second
  // piece of component state that could disagree with the mutation.
  const savingDraft = updateMutation.isPending && !(updateMutation.variables as any)?.review;
  const savingForReview = updateMutation.isPending && !!(updateMutation.variables as any)?.review;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.employee.deleteTicket(id),
    onSuccess: () => {
      setEditingTicket(null);
      invalidateTickets(queryClient);
      setMessage({ type: 'success', text: 'Ticket deleted' });
      setTimeout(clearTransientMessage, 3000);
    },
    onError: () => {
      setMessage({ type: 'error', text: 'Failed to delete ticket' });
      setTimeout(clearTransientMessage, 3000);
    },
  });

  const [voiceTranscript, setVoiceTranscript] = useState<string | null>(null);
  const [voiceConfidence, setVoiceConfidence] = useState<string | null>(null);

  const voiceMutation = useMutation({
    mutationFn: ({ jobId, audioBlob }: { jobId: string; audioBlob: Blob }) =>
      api.employee.voiceTicket(jobId, audioBlob),
    onSuccess: (res) => {
      const draft = res.data.data ?? res.data;
      setVoiceTranscript(draft.transcript ?? null);
      setVoiceConfidence(draft.confidence ?? null);
      if (editingTicket) {
        if (draft.workDate) editingTicket.work_date = draft.workDate;
        if (draft.startTime) editingTicket.start_time = draft.startTime;
        if (draft.endTime) editingTicket.end_time = draft.endTime;
        if (draft.endDate) editingTicket.end_date = draft.endDate;
        if (draft.actualMetresRun != null) editingTicket.actual_metres_run = draft.actualMetresRun;
        if (draft.notes) editingTicket.notes = draft.notes;
        setEditingTicket({ ...editingTicket });
      }
      setMessage({ type: 'success', text: `Voice input processed (${draft.confidence} confidence)` });
      setTimeout(clearTransientMessage, 5000);
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.error ?? 'Voice processing failed' });
    },
  });

  const submitMutation = useOfflineMutation(
    'tickets',
    'update',
    (vars: any) => api.employee.submitTicket(vars.id, vars.data),
    {
      invalidateKeys: [['field-my-tickets']],
      onSuccess: (res: any) => {
        setSubmitConfirm(false);
        setReview(null);
        setEditingTicket(null);
        invalidateTickets(queryClient);
        // field-approve carries line items, so it can carry the paid-pay
        // warning too (api#1352).
        const paidWarning = payAlreadyPaidMessage(res?.data);
        if (paidWarning) {
          setMessage({ type: 'warning', text: paidWarning, sticky: true });
          return;
        }
        setMessage({ type: 'success', text: isOnline ? 'Ticket submitted for approval' : 'Submit queued — will sync when back online' });
        setTimeout(clearTransientMessage, 3000);
      },
      onError: handleMutationError,
    }
  );

  useEffect(() => {
    if (createFromJob) {
      const newId = uuidv4();
      createMutation.mutate({ id: newId, data: { job_id: createFromJob, jobId: createFromJob, status: 'active' } });
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createFromJob]);

  /*
   * Open the ticket the job screen sent us to, once the list it lives in has
   * loaded.
   *
   * The row is taken from the same ['field-my-tickets'] payload the job screen
   * read, so it is the same object and no second fetch is needed. A miss is
   * reported rather than swallowed: silently landing on the full list would
   * read as a tap that did nothing, and the honest reading of a miss is that
   * the list moved under us — the cache refreshed, or another hand deleted the
   * draft — not that the tap failed.
   */
  useEffect(() => {
    if (!pendingOpenId || isLoading) return;
    const found = allTickets.find((t: any) => t?.id === pendingOpenId);
    setPendingOpenId(null);
    window.history.replaceState({}, document.title);
    if (found) {
      setActiveTab('all');
      setEditingTicket(found);
    } else {
      setMessage({ type: 'warning', text: "That ticket isn't in your list any more — pull down to refresh." });
      setTimeout(clearTransientMessage, 5000);
    }
  }, [pendingOpenId, isLoading, allTickets]);

  // Hydrate casing billing state when a ticket is opened (keyed on id so it
  // doesn't reset on every keystroke). Accepts camelCase or snake_case.
  useEffect(() => {
    if (!editingTicket) {
      setLineItems([]);
      setKnownLineItemIds(null);
      setAiDraftId(null);
      setLineItemErrors({});
      setLineItemUnitNotes({});
      setSlotPrefill({});
      setCrew([]);
      setEquipmentUsage([]);
      setSavedUsageState('unloaded');
      setAiReasoning(null);
      setAiConfidence(null);
      setLoadedTimeKey(null);
      return;
    }
    const t = editingTicket;
    setLoadedTimeKey(timeKeyOf(t));
    const rawLines = Array.isArray(t.lineItems) ? t.lineItems : (Array.isArray(t.line_items) ? t.line_items : []);
    // Row notes are keyed by index, so a wholesale replacement of the rows
    // invalidates every one of them.
    setLineItemUnitNotes({});
    setSlotPrefill({});
    // The baseline: the ids the SERVER handed us for this ticket, captured once
    // at open. Deliberately NOT derived from `lineItems` later — a row the hand
    // deletes leaves that array, and the whole point of the baseline is to
    // remember that we WERE handed it, so its absence from the payload reads as
    // a deletion rather than as a row we never saw.
    /*
     * ONLY claim a baseline when EVERY row we were handed carried an id.
     *
     * The first version of this was `.map(li => li.id).filter(Boolean)`, which
     * is the exact absent/empty mistake this feature exists to prevent, made
     * while implementing it. Rows without ids — a payload cached by a bundle
     * that predates this, or an API that does not return them — would reduce to
     * `[]`, and `[]` means "I was handed nothing": the save could then add rows
     * and could never DELETE one, silently, forever, on precisely the devices
     * least able to report it.
     *
     * `null` is the honest answer there. It means "I cannot identify these
     * rows", the key is omitted, and the API falls back to replacing the whole
     * set — which is what those rows were written under and what they still
     * mean. An empty ticket yields `[]` and that is correct: handed nothing,
     * may add, may delete nothing.
     */
    setAiDraftId(null);
    const handedIds = rawLines.map((li: any) => (li?.id != null ? String(li.id) : null));
    setKnownLineItemIds(handedIds.every((id: string | null) => id) ? (handedIds as string[]) : null);
    setLineItems(hydrateLineItems(rawLines));
    setCrew(Array.isArray(t.crew) ? t.crew : []);
    // `null`, not `[]`, when the key is absent — the two used to collapse into
    // the same empty array, which is precisely the distinction the prefill
    // needs. See SavedUsageState.
    const rawEq: any[] | null =
      Array.isArray(t.equipmentUsage) ? t.equipmentUsage
      : Array.isArray(t.equipment) ? t.equipment
      : null;
    setSavedUsageState(rawEq === null ? 'unloaded' : rawEq.length > 0 ? 'rows' : 'empty');
    setEquipmentUsage((rawEq ?? []).map(hydrateEquipmentUsageRow));
    setAiReasoning(null);
    setAiConfidence(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTicket?.id]);

  /**
   * Seed Equipment Used from the job's assigned equipment.
   *
   * The section's markup has been on this screen since the notes split, gated
   * on `equipmentUsage.length > 0`. Nothing ever filled that array: the create
   * path sends only a jobId, and the hydrate above read a key the API did not
   * return. So the gate never opened, the field never saw the control, and
   * every ticket written in the field saved zero rows — 34 of 34 on production,
   * 105 uncounted CAODC cert days across three live tenants.
   *
   * The ops editor has had this effect since it shipped (NewTicketScreen, from
   * job.equipment). This is the same idea with one guard it does not need,
   * because the two screens open different things: the ops editor seeds a
   * NEW ticket form, this one is the editor for a ticket that already exists
   * and may already have been answered. Hence `savedUsageState === 'empty'` —
   * seed only when the server has spoken AND said there is nothing.
   *
   * OFFLINE: `editingJob` is an online-only query and the IndexedDB `jobs`
   * store carries no equipment (GET /sync/pull does a flat table read with no
   * job_equipment_assignments join), so there is nothing to seed from in a dead
   * zone and this effect correctly does nothing. A hand who opens a ticket
   * offline still gets the section if the ticket already has rows, and their
   * toggles still sync — POST /sync/push now carries the nested payload.
   */
  useEffect(() => {
    if (!editingTicket || !editingJob) return;
    // 'unloaded' — we cannot tell an unanswered ticket from an unanswering
    // server. 'rows' — the hand already answered, and every-unit-off is an
    // answer. Only 'empty' is an invitation.
    if (savedUsageState !== 'empty') return;
    // A local edit already in flight outranks the job's list.
    if (equipmentUsage.length > 0) return;
    const jobEquipment: any[] = Array.isArray(editingJob.equipment) ? editingJob.equipment : [];
    if (jobEquipment.length === 0) return;
    setEquipmentUsage(jobEquipment.map(seedEquipmentUsageRow));
  }, [editingTicket, editingJob, savedUsageState, equipmentUsage.length]);

  // AI Suggest — online only. Mirrors the ops editor's aiPrefill consumption.
  const aiMutation = useMutation({
    mutationFn: (id: string) => api.employee.aiPrefillMyTicket(id),
    onSuccess: (res) => {
      const draft = res.data.data ?? res.data;
      const actualMetres = editingTicket?.actual_metres_run != null
        ? Number(editingTicket.actual_metres_run)
        : (draft.depthMetres != null ? Number(draft.depthMetres) : null);
      const items = (draft.lineItems ?? []).map((li: LineItem) => {
        const rate = li.rate != null ? Number(li.rate) : null;
        const unit = normalizeUnit(li.unit);
        // For per-metre items, prefer the field hand's actual metres over AI depth.
        const qty = (unit === 'per_metre' && actualMetres != null) ? actualMetres : (li.quantity != null ? Number(li.quantity) : null);
        const total = (rate != null && qty != null) ? Math.round(rate * qty * 100) / 100 : null;
        // The API links a drafted line only when exactly one item on the
        // resolved sheet has its name (0504, decided in code, not by the
        // model). Kept as sent; nulling it unlinked them all. `id` is decided below.
        return { ...li, rate, quantity: qty, total, unit, rateSheetItemId: readDraftRateSheetItemId(li) };
      });
      setLineItemUnitNotes({});
      setSlotPrefill({});
      // A drafted line that is the same service as a line already on the
      // ticket keeps that line's id, so the save updates the row in place
      // instead of deleting it and inserting a copy (which orphans its pay).
      // Every other drafted line is a new row. See lib/ai-draft-lines.
      // A discount is not a service line the draft could suggest, so replacing
      // the lines with the draft keeps the ticket's discounts, after the draft.
      setLineItems([...carryExistingLineIds<LineItem>(items, lineItems.filter(l => !isDiscountLine(l))), ...lineItems.filter(isDiscountLine)]);
      // Row marks are keyed by index and every row has just moved.
      setLineItemErrors({});
      setLineItemTypeErrors({});
      setAiDraftId(readPrefillDraftId(draft));
      if (Array.isArray(draft.crew) && draft.crew.length > 0) setCrew(draft.crew);
      setAiReasoning(draft.reasoning ?? null);
      setAiConfidence(draft.confidence ?? null);
      // Fold casing spec fields onto the ticket for the info header.
      setEditingTicket((prev: any) => prev ? {
        ...prev,
        customer_name: draft.customerName ?? prev.customer_name,
        rig_name: draft.rigName ?? prev.rig_name,
        lsd: draft.lsd ?? prev.lsd,
        // `wellName` was missing from this fold, and it is NOT the same bug as
        // the dropped save above — it fails one step EARLIER. The service has
        // returned it all along (voice-ticket.service.ts: `parsed.wellName ??
        // job.well_name`), but it never reached component state, so adding the
        // key to buildTicketBody would have saved `prev.well_name` — the job's
        // copy — and the spoken well would still be gone. Both lines are needed.
        well_name: draft.wellName ?? prev.well_name,
        notes: draft.notes ?? prev.notes,
        actual_metres_run: prev.actual_metres_run ?? (draft.depthMetres != null ? Number(draft.depthMetres) : prev.actual_metres_run),
        casing_size_inches: draft.casingSizeInches ?? prev.casing_size_inches,
        job_type: draft.jobType ?? prev.job_type,
      } : prev);
      // A full set of line items comes back even when nothing could price them
      // — the API prompt requires NEVER skip a service — so the count says
      // nothing about whether pricing worked. Read the reason.
      const notice = describeAiDraft(
        draft.lineItems?.length ?? 0,
        draft.confidence,
        readRateSheetResolution(draft)
      );
      setAiRateSheetChoice(notice.candidates);
      setMessage({ type: notice.tone, text: notice.text });
      if (notice.tone === 'success') setTimeout(clearTransientMessage, 5000);
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.error ?? 'AI Suggest failed — try again when you have signal.' });
    },
  });

  /**
   * Refuse the save if any line is incomplete, and put the reader in front of
   * the first bad one.
   *
   * Purely local, which is what makes it the right gate for this screen: a
   * hand on a lease with no signal gets the same refusal, at the same moment,
   * as one in the yard. The alternative — letting it queue and finding out on
   * sync — tells somebody hours later, on a different device.
   */
  const stringMetresPayload = () => buildCasingStringMetres(ticketStrings ?? [], recordedMetres, metresDraft);
  const validateStringMetres = (): boolean => {
    const r = stringMetresPayload();
    if (r.ok) return true;
    setMessage({ type: 'error', text: r.error });
    return false;
  };

  const validateEquipmentUsage = (): boolean => {
    const err = equipmentUsageError(equipmentUsage);
    if (!err) return true;
    setMessage({ type: 'error', text: err });
    return false;
  };

  const validateLineItems = (): boolean => {
    const result = checkLineItems(lineItems);
    setLineItemErrors(result.rowErrors);
    setLineItemTypeErrors(result.typeErrors);
    if (result.ok) return true;
    setMessage({ type: 'error', text: result.message! });
    if (result.firstInvalidIndex != null) {
      scrollRowIntoView(lineItemRowRefs.current[result.firstInvalidIndex]);
    }
    return false;
  };

  /**
   * Pick a tech for one slot — writes the id and the display initials together,
   * so a row can never carry one without the other. Mirrors NewTicketScreen.
   */
  const setLineItemTech = (index: number, slot: 1 | 2 | 3 | 4, userId: string | null) => {
    const member = findCrewById(allPickable(crewLists), userId);
    setLineItems(items => items.map((item, i) => i !== index ? item : assignTechSlot(item, slot, userId, member)));
    // A person has answered for this row; the suggestion mark comes off and
    // nothing is suggested on it again.
    setSlotPrefill(prev => { if (!(index in prev)) return prev; const next = { ...prev }; delete next[index]; return next; });
  };

  /**
   * The job-position suggestion for one row (lib/ticket-slot-prefill.ts). A row
   * still holding an untouched suggestion has it taken back first, so changing
   * the type re-suggests rather than leaving the last type's crew on the line;
   * a row a person has filled is not in `slotPrefill` and never reaches here.
   * No phase: tickets record none (see the helper).
   */
  const suggestSlots = (row: LineItem, mark: 'open' | 'prefilled') =>
    prefillTechSlots(mark === 'prefilled' ? clearTechSlots(row) : row, crew);

  /**
   * AI Suggest replaces the whole line item array when the draft lands (see
   * aiMutation). With rows already on the ticket that is a loss the hand has
   * to agree to first — and be told the size of. With none, no dialog: the
   * normal path stays one tap. Cancel fires no request.
   */
  // Discounts are kept across AI Suggest (see its onSuccess), so they are not
  // among the lines the dialog warns will be replaced.
  const aiReplaceCopy = describeAiReplace(lineItems.filter(l => !isDiscountLine(l)).length);
  const requestAiSuggest = () => {
    if (!editingTicket) return;
    if (aiReplaceCopy) {
      setAiReplaceConfirm(true);
      return;
    }
    aiMutation.mutate(editingTicket.id);
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    const mark = slotPrefill[index];
    if (field === 'lineItemType' && mark && lineItems[index]) {
      // Computed out here, not in an updater: it also sets the mark, and an
      // updater runs twice under StrictMode.
      const { line, prefilled } = suggestSlots({ ...lineItems[index], lineItemType: value }, mark);
      setLineItems(items => items.map((row, i) => (i === index ? line : row)));
      setSlotPrefill(prev => ({ ...prev, [index]: prefilled ? 'prefilled' : 'open' }));
    } else {
      setLineItems(items => items.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        if (field === 'rate' || field === 'quantity') {
          const r = field === 'rate' ? value : item.rate;
          const q = field === 'quantity' ? value : item.quantity;
          updated.total = r != null && q != null ? Math.round(Number(r) * Number(q) * 100) / 100 : null;
        }
        return updated;
      }));
    }
    /*
     * The mark comes off the moment the box has an answer in it — including a
     * typed 0, which `value != null` accepts and a truthiness test would not.
     * Scoped to the field that changed: a row can be refused for two things at
     * once, and clearing the whole row when the rate is filled would erase a
     * live description refusal and let the line read as corrected.
     */
    if ((field === 'description' || field === 'rate' || field === 'quantity') && value != null && value !== '') {
      setLineItemErrors(prev => clearFieldError(prev, index, field));
    }
    if (field === 'lineItemType' && lineItemTypeErrors[index]) {
      setLineItemTypeErrors(prev => { const next = { ...prev }; delete next[index]; return next; });
    }
    // The note says the picked unit could not be set automatically. Setting one
    // by hand is the thing it asks for, so it stops asking.
    if (field === 'unit' && lineItemUnitNotes[index]) {
      setLineItemUnitNotes(prev => { const next = { ...prev }; delete next[index]; return next; });
    }
  };

  /**
   * Fill this row from a price book item: Description, Rate, Revenue Code, and
   * Unit where the book's unit can be represented at all.
   *
   * TYPE IS NOT SET, deliberately — the rate sheet's item_type vocabulary and
   * the ticket's line_item_type enum are disjoint by design, and the row's own
   * "Type is required" check makes the hand choose. See lib/rate-sheet-items.ts.
   */
  // Off book on: the line is deliberately not a book item, so it holds no link
  // (a link and offBook together are a 400 on every write door). Off: a plain
  // unlinked line again. Never a block either way — see lib/price-book-link.
  const setLineOffBook = (index: number, offBook: boolean) => {
    setLineItems(items => items.map((row, i) => (
      i !== index ? row : offBook ? { ...row, offBook: true, rateSheetItemId: null } : { ...row, offBook: false }
    )));
  };

  // An exact name match on leaving the field links the line and nothing else:
  // the rate, unit and description stay as typed.
  const autoLinkLine = (index: number, item: RateSheetItem) => {
    setLineItems(items => items.map((row, i) => (
      i === index && !row.rateSheetItemId && row.offBook !== true ? { ...row, rateSheetItemId: item.id } : row
    )));
  };

  const pickRateSheetItem = (index: number, item: RateSheetItem) => {
    const current = lineItems[index];
    if (!current) return;
    // Applied once, out here — an updater that also assigns to something outside
    // itself runs twice under StrictMode and is the wrong place for the note.
    const { next: filled, unitNote } = applyRateSheetItem(current, item);
    setLineItems(items => items.map((row, i) => (i === index ? filled : row)));
    setLineItemUnitNotes(prev => {
      const next = { ...prev };
      if (unitNote) next[index] = unitNote; else delete next[index];
      return next;
    });
    // Field-scoped, because the price book fills a description and a rate but
    // never a quantity — clearing the whole row would take the mark off a
    // quantity box that is still empty.
    setLineItemErrors(prev => {
      let next = prev;
      if (filled.description?.trim()) next = clearFieldError(next, index, 'description');
      if (filled.rate != null) next = clearFieldError(next, index, 'rate');
      return next;
    });
  };

  const removeLineItem = (index: number) => {
    setLineItems(items => items.filter((_, i) => i !== index));
    // All three of these are keyed by ROW INDEX, so deleting a row shifts every
    // row after it and leaves its message pointing at the wrong line. Previously
    // only the two validation maps did this, and the red ring landing on an
    // innocent row read as a second bad line rather than a stale marker.
    setLineItemErrors(prev => reindexAfterRemoval(prev, index));
    setLineItemTypeErrors(prev => reindexAfterRemoval(prev, index));
    setLineItemUnitNotes(prev => reindexAfterRemoval(prev, index));
    setSlotPrefill(prev => reindexAfterRemoval(prev, index));
  };

  const addLineItem = () => {
    const blank: LineItem = {
      // No id: the server mints one on insert. Its absence from the baseline is
      // what tells the API this is a new row rather than an edit to a known one.
      id: null,
      // Deliberately unset — the operator picks a type. Defaulting to 'other'
      // silently sent every manual line down the AI-guess pay path.
      lineItemType: '',
      description: '',
      unit: '',
      rate: null,
      quantity: null,
      total: null,
      revenueCode: null,
      rateSheetItemId: null,
      assignedTech1Initials: null,
      assignedTech2Initials: null,
      assignedTech3Initials: null,
      assignedTech4Initials: null,
      assignedTech1Id: null,
      assignedTech2Id: null,
      assignedTech3Id: null,
      assignedTech4Id: null,
    };
    // A new row has no type yet, so this suggests nothing today — it is here so
    // a row created WITH a type is suggested the same as one typed afterwards.
    const { line, prefilled } = suggestSlots(blank, 'open');
    const index = lineItems.length;
    setLineItems(items => [...items, line]);
    setSlotPrefill(prev => ({ ...prev, [index]: prefilled ? 'prefilled' : 'open' }));
  };

  /** "Add discount" — one helper for both editors, lib/ticket-discount. */
  const addDiscount = (spec: DiscountSpec) => {
    const blank: LineItem = {
      id: null, lineItemType: 'other', description: '', unit: null, rate: null, quantity: null, total: null,
      revenueCode: null, rateSheetItemId: null,
      assignedTech1Initials: null, assignedTech2Initials: null, assignedTech3Initials: null, assignedTech4Initials: null,
      assignedTech1Id: null, assignedTech2Id: null, assignedTech3Id: null, assignedTech4Id: null,
    };
    setLineItems(items => [...items, buildDiscountLine(blank, spec, items)]);
  };

  const editDiscount = (index: number, edit: DiscountEdit) => {
    setLineItems(items => items.map((row, i) => (i === index ? editDiscountLine(row, edit, items) : row)));
    // Field-scoped, as for plain rows: a second-Multi-Rig refusal stays until
    // the line is removed, whatever else is typed on it.
    setLineItemErrors(prev => discountFieldsFor(edit).reduce((acc, f) => clearFieldError(acc, index, f), prev));
  };

  // Deliberately NOT sent:
  //   breakMinutes — removed from every API write path and calculation
  //                  (API #194). Hours are gross.
  //   totalHours   — the server recomputes it from workDate/endDate/
  //                  startTime/endTime on every write and logs+drops ours.
  const buildTicketBody = () => {
    const workDate = editingTicket?.work_date || editingTicket?.date;
    return {
      workDate,
      // Matches the Finish Date shown in the picker, which falls back to the
      // arrival date — what the hand sees is what gets written.
      endDate: calendarDayOrRaw(editingTicket?.end_date || workDate) || undefined,
      startTime: editingTicket?.start_time,
      endTime: editingTicket?.end_time,
      actualMetresRun: editingTicket?.actual_metres_run,
      // Location (API #476). Sent because the inputs below can now change it:
      // on a completions pad the well moves daily and the crew writes one
      // ticket per working day, so the location belongs to the ticket, not to
      // the job it was copied from.
      //
      // This is also what stops the voice draft being thrown away. AI Suggest
      // returns an `lsd` the hand actually spoke (voice-ticket.service.ts) and
      // the header renders it — and until this key existed the save dropped it
      // silently, so the hand saw the value they had just said and it never
      // reached the column.
      //
      // `provinceState` is deliberately NOT here. The API accepts it, but this
      // screen has no input for it, and round-tripping a field nobody can edit
      // means a stale local copy overwrites a concurrent office edit for no
      // benefit. Province is an office/settings-shaped value; the three below
      // are what a hand at the wellsite knows.
      lsd: editingTicket?.lsd,
      wellName: editingTicket?.well_name,
      rigName: editingTicket?.rig_name,
      // AFE and Well Licence (API 0394). Same argument as the three above,
      // arriving from the billing side rather than the location side: an AFE is
      // per WELL, and on a pad one job spans several — TNT J-4315 is one job,
      // eleven tickets, seven wells. The job's value prefilled this ticket at
      // creation and is right for the 32-of-42 single-well case; when it is
      // not, the hand standing on the location is the one who has the paperwork.
      //
      // rigNumber is deliberately absent, like provinceState above: the API
      // refuses it on every door because it is per JOB, so round-tripping it
      // would send a key that is silently dropped.
      afeNumber: editingTicket?.afe_number,
      wellLicence: editingTicket?.well_licence,
      notes: editingTicket?.notes,
      customerNotes: editingTicket?.customer_notes || undefined,
      photoUrls: editingTicket?.photo_urls ?? [],
      /*
       * ALWAYS AN ARRAY, AND NEVER `undefined` WHEN THE HAND HAS NO ROWS LEFT.
       *
       * This read `lineItems.length > 0 ? lineItems : undefined`, which is the
       * same absent/empty conflation the baseline below is written to avoid, made
       * one key earlier. `undefined` is dropped by JSON, so a hand who removed
       * the ONLY line on a ticket sent no `lineItems` key at all — and every
       * server door reads an absent key as "this save is not speaking about the
       * lines, leave them alone". The row survived, the save reported success,
       * and it was still there when he reopened the ticket. Measured on the
       * real screen: the payload went out as `{"knownLineItemIds":["row-a"]}`
       * and nothing else.
       *
       * The distinction is the SAME one `knownLineItemIds` draws, on the other
       * half of the same sentence:
       *
       *   no key   this save says nothing about the lines
       *   []       the hand cleared them
       *
       * Every door already honours it and says so in its own words — sync push:
       * "an ABSENT key means the client is not speaking about this ticket's
       * lines and they must be left alone; an explicit `[]` is the hand
       * clearing them". `Array.isArray(body.lineItems)` is the read on all four
       * (PUT /employee/my-tickets/:id, POST .../field-approve, and sync push's
       * create and update). Nothing on the server needed changing; the client
       * simply could not express the second row of that map.
       *
       * An empty set on a ticket the hand has not emptied cannot arise here:
       * the only way to reach zero rows is to remove them, and a ticket opened
       * with none sends `knownLineItemIds: []` alongside, which the API reads
       * as "handed nothing, may add, may delete nothing".
       *
       * Sent as the wire shape (lib/ticket-discount `lineForWire`): a plain line
       * goes out exactly as it is held; a discount line goes out with its
       * discount keys in snake_case and no crew. Still always an array.
       */
      lineItems: shownLines.map(lineForWire),
      /*
       * "This client knows about discount lines." Without it the API keeps any
       * stored discount the body leaves out, because an older bundle would leave
       * one out by not knowing it exists — so a hand removing the ticket's ONLY
       * discount (lineItems: []) would see the save succeed and the line come
       * back. Sent on every body this function builds: the PUT, field-approve,
       * and every offline save, which the queue replays with this body as-is.
       */
      discountAware: true,
      /*
       * WHICH ROWS THIS SAVE IS SPEAKING FOR.
       *
       * Without it the API replaces the ticket's whole line-item set, which is
       * what it has always done and what every un-updated bundle still gets.
       * With it, rows this device was never handed — the computer hand's
       * charges, added while this editor was open — are left alone instead of
       * being deleted by omission.
       *
       * `?? undefined` and never `?? []`: absent means "replace everything",
       * `[]` means "I hold nothing". Collapsing them would make a line
       * undeletable on a device that was handed none.
       */
      knownLineItemIds: knownLineItemIds ?? undefined,
      // Only when AI Suggest ran in this session; absent otherwise. The API
      // refuses a draft id it cannot link, so never send '' or a guess.
      draftId: aiDraftId ?? undefined,
      // Serialized, not the screen's rows: hours/runs keys appear only when the
      // hand typed (or cleared) one — see lib/equipment-usage.ts.
      equipmentUsage: equipmentUsage.length > 0 ? serializeEquipmentUsage(equipmentUsage) : undefined,
      // Per-string metres (api#830). NO KEY when there is nothing to say — see
      // buildCasingStringMetres — and absent, not `undefined`: the offline
      // mutation spreads this body over the device's copy of the ticket, and an
      // `undefined` would wipe the recorded list there. The API writes
      // actual_metres_run as their sum.
      ...(() => {
        const r = stringMetresPayload();
        return r.ok && r.entries.length > 0 ? { casingStringMetres: r.entries } : {};
      })(),
    };
  };

  // ─── THE FIELD-SIDE EDIT GATE, AND WHY IT IS NOT A ONE-LINE LIST ───────────
  //
  // This list is the SCREEN's copy of a rule the SERVER owns. The server's rule
  // is FROZEN_STATUSES in the API's src/lib/ticket-status.ts — 'customer_approved'
  // and 'invoiced', and nothing else. Everything short of that is writable
  // through PUT /employee/my-tickets/:id.
  //
  // For months this screen said draft | active | rejected, which is narrower,
  // and that was never a decision. It was born as a mechanical refactor of the
  // literal `status === 'draft'` (FE 3fd54e4, "use correct DB status values" —
  // a vocabulary correction), then extended once, reactively, to re-admit
  // 'draft' (fc38280). Neither commit mentions signing, approval or a customer.
  // The API held this exact list and abandoned it for a named reason: "Previously
  // scalars required draft/active/rejected, which locked a field tech out of a
  // ticket they had just field-approved."
  //
  // Measured 2026-09-10: 32 live tickets sat in the band where the server would
  // accept an edit and this screen hid the inputs — 18 field_approved,
  // 13 office_approved, 1 customer_rejected. TNT held 5 of them.
  //
  // NOBODY HAS SIGNED at either of the two we open. This screen captures no
  // signature at all; its Submit modal says "goes to the office for approval".
  // The customer's name and title land at 'customer_approved'
  // (ticket-approval.routes.ts), which IS frozen. So "someone signed it" is not
  // an argument against editing a field_approved or office_approved ticket.
  //
  //   field_approved   — the clean case. The DB status trigger permits
  //                      field_approved → field_approved as a same-status
  //                      no-op, specifically so a hand can re-submit an edited
  //                      ticket. Plain widen, no gate.
  //
  //   office_approved  — writable, but an edit REVERTS the ticket to
  //                      field_approved and undoes approval's side effects
  //                      (revertOfficeApprovalForEdit). That is a consequence
  //                      the hand must be told about BEFORE he types, so it is
  //                      gated on requestEditUnlock below rather than opening
  //                      straight into a form.
  //
  //   customer_rejected — DELIBERATELY NOT HERE, and it is the mutation the
  //                      next reader will make, because leaving it out looks
  //                      like an oversight beside 'rejected'. PUT accepts it;
  //                      POST .../field-approve REFUSES it ("Cannot
  //                      field-approve a ticket in customer_rejected status" —
  //                      it must be returned to draft first). Widening this
  //                      list alone would render a full editable form whose
  //                      Submit is refused by the server. Pinned by
  //                      "a customer-rejected ticket does not open an editable
  //                      form" in FieldTicketsScreen.edit-gate.test.tsx.
  //
  // 'active' is gone from the list, and NOT by simply deleting the literal. The
  // API records it as dead ("no ticket has it and normalizeLegacyStatus maps it
  // to draft"), but this screen can still be handed one from an IndexedDB record
  // written by an older build — see LEGACY_STATUS_ALIASES. Deleting the literal
  // bare would quietly make such a record read-only. Normalising first removes
  // the dead status from the gate AND keeps the legacy record editable, and it
  // makes this gate speak the same vocabulary the badges and tabs already do
  // (both go through ticketStatusGroup, which normalises). One place knows about
  // 'active'; this is not it.
  const isEditable = (status: string) =>
    ['draft', 'rejected', 'field_approved', 'office_approved']
      .includes(normalizeTicketStatus(status));

  // office_approved is the one editable status whose edit costs something the
  // hand cannot see. He asks for the form; he is told what asking does; then he
  // types. Keyed on the ticket id rather than a bare boolean so closing the
  // ticket and reopening it asks again.
  const needsEditUnlock = (status: string) => normalizeTicketStatus(status) === 'office_approved';
  /*
   * WHICH TICKETS OFFER A DELETE BUTTON — THE SERVER'S RULE, NOT A WIDER ONE.
   *
   * The server's rule is one literal, in the UPDATE itself:
   * `DELETE /employee/my-tickets/:id` carries `AND tickets.status = 'draft'`
   * (employee.routes.ts), and a ticket past draft is refused with
   * "Only a draft ticket can be deleted — this one is '<status>'."
   *
   * This screen offered the button on draft | active | submitted |
   * field_approved. Measured against production 2026-09-11: 21 live tickets
   * sat in that gap — all field_approved — each rendering a red Delete a hand
   * could press, confirm, and be refused on. None in 'active' or 'submitted',
   * so narrowing costs nothing that works today.
   *
   * Nobody had reported it because nobody was using the field editor in
   * anger yet; api#689 widened ticket writes to the whole job crew, so the
   * population about to meet this button is every hand on every job rather
   * than one designated writer.
   *
   * THE CLIENT NARROWS; THE SERVER DOES NOT WIDEN. A hand deleting a
   * field_approved ticket would be deleting a document that has already gone
   * to the office, and `GET /employee/my-tickets` filters `deleted_at IS NULL`
   * — there is no screen on which the loss can be found again. The gap was
   * a button that lied, not a rule that was too strict.
   *
   * Normalised rather than compared raw, for the same reason `isEditable`
   * above is: an offline record written by an older build can carry 'active',
   * which the canonical vocabulary — and the API's own normalizeLegacyStatus —
   * reads as draft. Comparing the raw string would make a real draft held in
   * IndexedDB under the dead spelling silently undeletable. One place knows
   * about 'active'; this is not it.
   *
   * NOT the same question as `isEditable`, and the two are deliberately far
   * apart: the server takes an EDIT on four statuses and a DELETE on one.
   */
  const isDeletable = (status: string) => normalizeTicketStatus(status) === 'draft';

  // Badge + tabs both resolve through ticketStatusGroup, so a status can never
  // be badged "Submitted" while the Submitted tab refuses to show it.
  const statusIcon = (status: string) => {
    switch (ticketStatusGroup(status)) {
      case 'draft': return <Edit3 size={16} className="text-amber-400" />;
      case 'submitted': return <Clock size={16} className="text-blue-400" />;
      case 'approved': return <CheckCircle size={16} className="text-green-400" />;
      case 'invoiced': return <CheckCircle size={16} className="text-purple-400" />;
      case 'rejected': return <AlertTriangle size={16} className="text-red-400" />;
      default: return <Clock size={16} className="text-fieldiq-muted" />;
    }
  };

  const statusColor = (status: string) => {
    switch (ticketStatusGroup(status)) {
      case 'draft': return 'bg-amber-500/20 text-amber-400';
      case 'submitted': return 'bg-blue-500/20 text-blue-400';
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      case 'invoiced': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-fieldiq-dark text-fieldiq-text';
    }
  };

  const statusLabel = (status: string) => fieldTicketStatusLabel(status);

  // work_date/end_date are calendar days, not instants. This local body is the
  // implementation src/lib/calendar-date.ts says it was lifted from — it has
  // been the copy, not the original, since the day of the lift.
  const formatDate = (dateStr: string | null | undefined) =>
    formatCalendarDate(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });

  const formatTime = (timeStr: string | null | undefined) => {
    if (!timeStr) return null;
    // Handle "07:00:00" → "7:00 AM"
    try {
      const [h, m] = timeStr.split(':');
      const hour = parseInt(h);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${h12}:${m} ${ampm}`;
    } catch { return timeStr; }
  };

  /* ─── Review view ──────────────────────────────────────────────────────────
   *
   * The third view of this screen, between the editor and the office. Read
   * only, by construction: it renders `review`, which is the save response,
   * and holds no editable state of its own.
   *
   * The body is `FieldTicketDisplay` — the same component the office editor
   * previews with and the customer approves from — so a hand and the office
   * are not looking at two renderings of one ticket. It is a white document
   * card on a dark screen on purpose: it IS the document, and the point of the
   * step is to show the hand the thing that leaves the field.
   */
  if (editingTicket && review) {
    const jobLabel =
      editingTicket.job_number || editingTicket.jobNumber || editingJobNumber ||
      editingTicket.job_id?.slice(0, 8) || '—';
    const ticketLabel = editingTicket.ticket_number || editingTicket.ticketNumber || 'Ticket';

    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
        <div className="max-w-[1920px] mx-auto">
          <Header />
          <div className="px-4 py-3 border-b border-fieldiq-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setReview(null)}
                aria-label="Back to editing"
                className="text-fieldiq-text"
              >
                <ArrowLeft size={26} />
              </button>
              <h1 className="text-xl font-bold text-white">Review Ticket</h1>
            </div>
            <span className="text-sm text-fieldiq-muted">{ticketLabel}</span>
          </div>

          <div className="px-4 py-4 space-y-4">
            {message && (
              <div className={`p-3 rounded-lg text-sm ${
                message.type === 'success' ? 'bg-green-500/20 text-green-400'
                : message.type === 'warning' ? 'bg-amber-500/20 text-amber-400'
                : 'bg-red-500/20 text-red-400'}`}>
                {message.text}
              </div>
            )}

            <p className="text-sm text-fieldiq-text">
              Check this over before it goes in. Tap Back to change anything.
            </p>

            <FieldTicketDisplay
              ticketNumber={ticketLabel}
              status={statusLabel(editingTicket.status)}
              companyName={company?.name || ''}
              customerName={editingTicket.customer_name || editingTicket.customerName || '—'}
              jobType={editingTicket.job_type || editingTicket.jobType}
              rigName={editingTicket.rig_name || editingTicket.rigName}
              metresRun={editingTicket.actual_metres_run ?? undefined}
              workDate={formatDate(editingTicket.work_date ?? editingTicket.date) ?? ''}
              startTime={formatTime(editingTicket.start_time) ?? undefined}
              endTime={formatTime(editingTicket.end_time) ?? undefined}
              /* Server-computed hours, blank when the server has none. A
                 locally-derived figure here would be a second answer. */
              totalHours={formatTicketHours(editingTicket.total_hours ?? editingTicket.totalHours) ?? ''}
              lineItems={review.lineItems.map((li) => ({
                type: li.type,
                description: li.description || getLineItemTypeLabel(li.type),
                quantity: li.quantity,
                unit: li.unit,
                rate: li.rate,
                total: li.total,
              }))}
              /* Every figure below is the server's. Null renders '—'. */
              subtotal={review.totals.subtotal}
              taxRate1Label={review.totals.tax1Label ?? undefined}
              taxRate1Amount={review.totals.tax1Amount}
              taxRate2Label={review.totals.tax2Label ?? undefined}
              taxRate2Amount={review.totals.tax2Amount}
              total={review.totals.total}
              currency={currency}
              customerNotes={editingTicket.customer_notes || editingTicket.customerNotes}
              crew={crew.map((c: any) => ({ name: crewLabel(c), role: c.role || '' }))}
            />

            {!review.totals.fromServer && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 text-amber-400 text-sm">
                <WifiOff size={16} className="mt-0.5 flex-shrink-0" />
                <span>
                  Totals calculate when you're back online. Your changes are saved
                  and this ticket will send itself when you have signal.
                </span>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setReview(null)}
                className="flex-1 bg-fieldiq-card text-white font-semibold py-4 rounded-xl border border-fieldiq-border min-h-[56px]"
              >
                Back / Edit
              </button>
              <button
                onClick={() => setSubmitConfirm(true)}
                disabled={submitMutation.isPending}
                className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-semibold py-4 rounded-xl flex items-center justify-center gap-2 min-h-[56px] disabled:opacity-50"
              >
                {submitMutation.isPending ? (
                  <><Loader size={18} className="animate-spin" /> Submitting...</>
                ) : (
                  <><Send size={18} /> Submit Ticket</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/*
          * The only door to submitTicket(). Cancel closes it and leaves the
          * hand on the review step with nothing sent.
          */}
        <ConfirmModal
          isOpen={submitConfirm}
          title="Submit Ticket"
          confirmLabel="Confirm"
          confirmVariant="primary"
          loading={submitMutation.isPending}
          onCancel={() => setSubmitConfirm(false)}
          onConfirm={() => {
            const body = { ...buildTicketBody(), status: 'field_approved' };
            submitMutation.mutate({
              id: editingTicket.id,
              version: editingTicket.version,
              data: body,
            });
          }}
          body={
            <div className="space-y-2">
              <div className="flex justify-between gap-4">
                <span className="text-fieldiq-muted">Ticket</span>
                <span className="text-white font-medium">{ticketLabel}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-fieldiq-muted">Job</span>
                <span className="text-white font-medium">{jobLabel}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-fieldiq-muted">Total</span>
                <span className="text-white font-medium">
                  {fmtCurrency(review.totals.total, currency)}
                </span>
              </div>
              <p className="pt-1">
                Once submitted this ticket goes to the office for approval.
              </p>
            </div>
          }
        />
      </div>
    );
  }

  // Editing / Detail view
  if (editingTicket) {
    // Two conditions, not one. The server would take the write in both cases;
    // the second is about the hand knowing what the write costs before he makes
    // it, not about permission.
    const locked = needsEditUnlock(editingTicket.status) && editUnlockedFor !== editingTicket.id;
    const editable = isEditable(editingTicket.status) && !locked;
    const deletable = isDeletable(editingTicket.status);

    // Arrival = work_date/start_time, Finish = end_date/end_time. Finish Date
    // falls back to the arrival date so the picker always shows what will be
    // sent as `endDate` — no hidden value.
    // The fallback is the TENANT's today. `?? ''` only seeds the picker: with
    // no zone there is no honest today, so the picker shows empty.
    const arrivalDate = editingTicket.work_date ?? editingTicket.date ?? todayCalendarDate(timeZone) ?? '';
    const finishDate = calendarDayOrRaw(editingTicket.end_date ?? arrivalDate);
    const serverHours = formatTicketHours(editingTicket.total_hours ?? editingTicket.totalHours);
    const timesContradict = hasContradictoryTimes(
      arrivalDate, finishDate, editingTicket.start_time, editingTicket.end_time
    );
    // Hours come from the server, so an unsaved date/time edit makes the
    // displayed number stale — say so rather than showing a number that
    // doesn't match the times on screen.
    const timesDirty = loadedTimeKey != null && loadedTimeKey !== timeKeyOf(editingTicket);

    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
        <div className="max-w-[1920px] mx-auto">
          <Header />
          <div className="px-4 py-3 border-b border-fieldiq-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setEditingTicket(null)} className="text-fieldiq-text">
                <ArrowLeft size={26} />
              </button>
              <h1 className="text-xl font-bold text-white">
                {editable ? 'Edit Ticket' : 'View Ticket'}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {/* The way IN to an office-approved ticket. It opens the warning,
                  never the form — the hand is told what editing does before he
                  has typed anything, not in the Submit modal by which point he
                  has filled the whole thing in. Absent for every other status:
                  a draft or a field-approved ticket opens straight into its
                  form, exactly as before. */}
              {locked && (
                <button
                  onClick={() => setEditUnlockConfirm(true)}
                  className="flex items-center gap-1.5 bg-fieldiq-card border border-fieldiq-border text-white text-sm font-semibold px-3 py-2 rounded-lg hover:border-fieldiq-muted transition-colors"
                >
                  <Edit3 size={14} /> Edit ticket
                </button>
              )}
              {editable && editingTicket.job_id && (
                <Tooltip content={!isOnline ? 'Connect to use AI Suggest' : undefined}>
                  <button
                    onClick={requestAiSuggest}
                    disabled={!isOnline || aiMutation.isPending}
                    className="flex items-center gap-1.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-purple-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {aiMutation.isPending ? (
                      <><Loader size={14} className="animate-spin" /> Thinking...</>
                    ) : (
                      <><Sparkles size={14} /> AI Suggest</>
                    )}
                  </button>
                </Tooltip>
              )}
              {deletable && (
                /*
                 * Named, for the same reason the per-line trash above is: an
                 * icon-only button has no accessible name at all, so a screen
                 * reader announces nothing and a test can only reach it by
                 * walking the DOM — and a header holding several icon buttons
                 * makes that walk follow whichever one moved. An assertion
                 * against an unnamed button finds nothing in EVERY state, so it
                 * passes just as happily against a button that is still there.
                 */
                <button
                  aria-label="Delete this ticket"
                  onClick={() => {
                    if (window.confirm('Delete this draft ticket?')) {
                      deleteMutation.mutate(editingTicket.id);
                    }
                  }}
                  className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          </div>
          {editable && editingTicket.job_id && (
            <div className="px-4 pt-2">
              <VoiceRecorder
                onRecordingComplete={(blob) =>
                  voiceMutation.mutate({ jobId: editingTicket.job_id, audioBlob: blob })
                }
                isProcessing={voiceMutation.isPending}
              />
            </div>
          )}
        </div>

        <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
          {message && (
            /*
             * Named so a test can ask whether the banner is still up without
             * matching on what it says — the two are separate behaviours and a
             * test that reads the wording cannot tell them apart. Same reason
             * the rows carry `line-item-row`.
             */
            <div
              ref={editorMessageRef}
              data-testid="editor-message"
              className={`rounded-xl p-3 flex items-center gap-2 ${
              message.type === 'success' ? 'bg-green-500/20 text-green-400'
              : message.type === 'warning' ? 'bg-amber-500/20 text-amber-400'
              : 'bg-red-500/20 text-red-400'}`}>
              {message.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
              <span className="text-sm flex-1">{message.text}</span>
              {message.sticky && (
                <button type="button" onClick={() => setMessage(null)} aria-label="Dismiss" className="shrink-0 px-1 font-bold hover:opacity-70">
                  &times;
                </button>
              )}
            </div>
          )}

          {voiceTranscript && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Info size={16} className="text-blue-400" />
                <h3 className="text-sm font-bold text-blue-400">What I heard</h3>
                {voiceConfidence && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    voiceConfidence === 'high' ? 'bg-green-500/20 text-green-400' :
                    voiceConfidence === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {voiceConfidence} confidence
                  </span>
                )}
              </div>
              <p className="text-sm text-fieldiq-text italic">"{voiceTranscript}"</p>
            </div>
          )}

          {/* Rate sheet ambiguity. A field hand cannot pick the sheet — that is an
              office decision — so this explains the blank rates and tells them
              the ticket is still submittable rather than broken. */}
          {aiRateSheetChoice.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-amber-400" />
                <h3 className="text-sm font-bold text-amber-400">Rates left blank</h3>
              </div>
              <p className="text-sm text-fieldiq-text mb-2">
                {aiRateSheetChoice.length} rate sheets cover this job and they price the work
                differently, so nothing could pick one automatically. Fill in the hours and
                quantities as normal — the office will add the prices.
              </p>
              <ul className="text-sm text-fieldiq-text space-y-1">
                {aiRateSheetChoice.map((s) => (
                  <li key={s.id} className="flex items-center gap-2">
                    <span className="text-amber-400">•</span>
                    <span>{s.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* AI reasoning / confidence */}
          {aiReasoning && (
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={16} className="text-purple-400" />
                <h3 className="text-sm font-bold text-purple-400">AI Analysis</h3>
                {aiConfidence && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    aiConfidence === 'high' ? 'bg-green-500/20 text-green-400' :
                    aiConfidence === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {aiConfidence} confidence
                  </span>
                )}
              </div>
              <p className="text-sm text-fieldiq-text">{aiReasoning}</p>
            </div>
          )}

          {/* Ticket info header */}
          <div className="bg-fieldiq-card rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {editingTicket.ticket_number || editingTicket.ticketNumber || 'New Ticket'}
                </h2>
                <p className="text-sm text-fieldiq-muted">
                  Job: {editingTicket.job_number || editingTicket.jobNumber || editingJobNumber || editingTicket.job_id?.slice(0, 8) || '—'}
                </p>
                {/* Last-write-wins, and this screen can sync an offline edit an
                    hour late. The stamp is how a hand sees the office got here
                    first. Absolute inline — touch screens have no hover. */}
                <LastEditedStamp
                  ticket={liveEditingRow ?? editingTicket}
                  absolute="inline"
                  className="mt-1"
                />
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(editingTicket.status)}`}>
                {statusLabel(editingTicket.status)}
              </span>
            </div>

            {/* Summary info — customer/spec details (some populated by AI Suggest) */}
            {(editingTicket.customer_name || editingTicket.lsd || editingTicket.work_date || editingTicket.casing_size_inches || editingTicket.job_type) && (
              <div className="grid grid-cols-2 gap-3 pt-1">
                {editingTicket.customer_name && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">Customer</span>
                    <span className="text-sm text-white">{editingTicket.customer_name}</span>
                  </div>
                )}
                {/* Gated on !editable, matching Arrival below: when the edit
                    block is open these are INPUTS a few rows down, and showing
                    the same value twice — once live, once frozen — is how a
                    hand comes to believe they changed something they did not. */}
                {!editable && editingTicket.lsd && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">LSD</span>
                    <span className="text-sm text-white">{editingTicket.lsd}</span>
                  </div>
                )}
                {/* Label ABOVE value, matching every sibling cell in this grid.
                    These two shipped with `flex items-center gap-1.5`, which put
                    the label beside the value — fine at desk width and wrong on a
                    phone, which is the only place this view is used. At 390px the
                    label and the value each wrapped to two lines inside a 157px
                    cell: "AFE /" / "PO #" against "AFE-PAD-N30-" / "0001", four
                    lines where every neighbour uses two. Measured, not eyeballed:
                    Customer, LSD, Well and Hours all carry NO wrapper class, and
                    these were the only two that did. */}
                {!editable && editingTicket.afe_number && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">AFE / PO #</span>
                    <span className="text-sm text-white">{editingTicket.afe_number}</span>
                  </div>
                )}
                {!editable && editingTicket.well_licence && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">Well Licence</span>
                    <span className="text-sm text-white">{editingTicket.well_licence}</span>
                  </div>
                )}
                {!editable && editingTicket.well_name && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">Well</span>
                    <span className="text-sm text-white">{editingTicket.well_name}</span>
                  </div>
                )}
                {editingTicket.casing_size_inches && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">Pipe Size</span>
                    <span className="text-sm text-white">{editingTicket.casing_size_inches}"</span>
                  </div>
                )}
                {editingTicket.job_type && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">Job Type</span>
                    <span className="text-sm text-white">{getJobTypeLabel(String(editingTicket.job_type))}</span>
                  </div>
                )}
                {!editable && editingTicket.work_date && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">Arrival</span>
                    <span className="text-sm text-white">
                      {formatDate(arrivalDate)}{editingTicket.start_time ? ` · ${formatTime(editingTicket.start_time)}` : ''}
                    </span>
                  </div>
                )}
                {!editable && editingTicket.end_time && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">Finish</span>
                    <span className="text-sm text-white">
                      {formatDate(finishDate)} · {formatTime(editingTicket.end_time)}
                    </span>
                  </div>
                )}
                {!editable && (
                  <div>
                    <span className="text-xs text-fieldiq-muted block">Hours</span>
                    {serverHours ? (
                      <span className="text-sm text-white">{serverHours}</span>
                    ) : timesContradict ? (
                      <span className="text-sm text-red-400">Times contradict — finish before arrival</span>
                    ) : (
                      <span className="text-sm text-fieldiq-muted">Not calculated</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {editable ? (
            <div className="bg-fieldiq-card rounded-xl p-4 space-y-4">
              {/* Arrival / Finish pairs — mirrors NewTicketScreen.tsx (ops editor)
                  field order, labels and grouping so both views read alike. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <Calendar size={12} /> Arrival Date
                  </label>
                  <DatePicker
                    value={arrivalDate}
                    onChange={v => setEditingTicket({ ...editingTicket, work_date: v, date: v })}
                  />
                </div>
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <Clock size={12} /> Arrival Time
                  </label>
                  <TimePicker
                    value={editingTicket.start_time ?? ''}
                    onChange={v => setEditingTicket({ ...editingTicket, start_time: v })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <Calendar size={12} /> Finish Date
                  </label>
                  <DatePicker
                    value={finishDate}
                    onChange={v => setEditingTicket({ ...editingTicket, end_date: v })}
                  />
                </div>
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <Clock size={12} /> Finish Time
                  </label>
                  <TimePicker
                    value={editingTicket.end_time ?? ''}
                    onChange={v => setEditingTicket({ ...editingTicket, end_time: v })}
                  />
                </div>
              </div>

              <TicketHoursReadout
                hours={serverHours}
                contradictory={timesContradict}
                dirty={timesDirty}
              />

              {/* Location — editable from the field (API #476).
                  Free text on purpose: `jobs.well_id_lsd` already holds
                  'Pad 21', 'Fort Sask' and 'Fort Saskatchewan' across live TNT
                  jobs, and which of those name the same place is the customer's
                  business fact, not something this form should fold. No
                  pattern, no datalist, no uppercase-on-blur. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <MapPin size={12} /> LSD
                  </label>
                  <input
                    type="text"
                    value={editingTicket.lsd ?? ''}
                    onChange={e => setEditingTicket({ ...editingTicket, lsd: e.target.value })}
                    placeholder="e.g. 03-19-049-12W5 or Pad 21"
                    className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <MapPin size={12} /> Well
                  </label>
                  <input
                    type="text"
                    value={editingTicket.well_name ?? ''}
                    onChange={e => setEditingTicket({ ...editingTicket, well_name: e.target.value })}
                    placeholder="e.g. 100/06-07-049-12W5"
                    className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                  />
                </div>
              </div>
              {/* AFE and Well Licence sit directly under the well they belong
                  to, because that adjacency IS the feature: on a pad the well
                  changes per ticket and the AFE changes with it. Free text and
                  no maxLength beyond the column width — an AER licence, a BC WA
                  and a Saskatchewan number are three shapes, and refusing an
                  operator's own reference is worse than storing an odd one. */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <FileText size={12} /> AFE / PO #
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={editingTicket.afe_number ?? ''}
                    onChange={e => setEditingTicket({ ...editingTicket, afe_number: e.target.value })}
                    placeholder="From the company man"
                    className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block flex items-center gap-1">
                    <FileText size={12} /> Well Licence
                  </label>
                  <input
                    type="text"
                    maxLength={50}
                    value={editingTicket.well_licence ?? ''}
                    onChange={e => setEditingTicket({ ...editingTicket, well_licence: e.target.value })}
                    placeholder="Licence / WA number"
                    className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block">Rig</label>
                  <input
                    type="text"
                    value={editingTicket.rig_name ?? ''}
                    onChange={e => setEditingTicket({ ...editingTicket, rig_name: e.target.value })}
                    placeholder="Rig name"
                    className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-fieldiq-muted mb-1 block">Metres Run</label>
                  {/* Once the ticket holds per-string metres, its total is the
                      server's sum of them (api#830) and is shown, not typed —
                      a typed total would be overwritten on save. Until then the
                      single total works exactly as it always has. */}
                  {totalIsStringSum(recordedMetres) ? (
                    <p className="px-3 py-2 text-sm text-white" data-testid="metres-run-total">
                      {fmtQty(editingTicket.actual_metres_run) === '—' ? '—' : `${fmtQty(editingTicket.actual_metres_run)} m`}
                    </p>
                  ) : (
                    <input
                      type="number"
                      onWheel={blurOnWheel}
                      value={editingTicket.actual_metres_run ?? ''}
                      onChange={e => setEditingTicket({ ...editingTicket, actual_metres_run: e.target.value ? parseFloat(e.target.value) : null })}
                      placeholder="Total metres"
                      className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                    />
                  )}
                </div>
              </div>
              {hasStringInputs && (
                <CasingStringMetresFields
                  strings={ticketStrings ?? []}
                  draft={metresDraft}
                  onChange={(id, v) => setMetresDraft(d => ({ ...d, [id]: v }))}
                  recorded={recordedMetres}
                  totalFromApi={editingTicket.actual_metres_run}
                  labelClassName="text-xs text-fieldiq-muted mb-1 block"
                  inputClassName="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
                />
              )}
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">Internal Notes</label>
                <textarea
                  rows={2}
                  value={editingTicket.notes ?? ''}
                  onChange={e => setEditingTicket({ ...editingTicket, notes: e.target.value })}
                  placeholder="Internal only — not visible to customers"
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none resize-none"
                />
              </div>
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">Customer Notes</label>
                <textarea
                  rows={2}
                  value={editingTicket.customer_notes ?? ''}
                  onChange={e => setEditingTicket({ ...editingTicket, customer_notes: e.target.value })}
                  placeholder="Visible to customer on approval page"
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none resize-none"
                />
              </div>

              {/* Equipment Used */}
              {equipmentUsage.length > 0 && (
                <div className="bg-fieldiq-dark rounded-xl border border-fieldiq-border p-4">
                  <h3 className="text-sm font-bold flex items-center gap-2 mb-3">
                    <Wrench size={16} className="text-fieldiq-accent" />
                    Equipment Used ({equipmentUsage.filter(e => e.wasUsed).length}/{equipmentUsage.length})
                  </h3>
                  <div className="space-y-2">
                    {equipmentUsage.map((item, i) => (
                      <div
                        key={item.equipmentId}
                        // Named, not just drawn. The toggle, the day count and
                        // the steppers only mean anything attached to a unit,
                        // and without this the row is an anonymous div a screen
                        // reader reads as four unrelated controls.
                        role="group"
                        aria-label={item.name || 'Equipment'}
                        className={`flex flex-wrap items-center gap-3 bg-fieldiq-card rounded-xl px-4 py-3 border border-fieldiq-border ${!item.wasUsed ? 'opacity-50' : ''}`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setEquipmentUsage(prev => prev.map((eq, j) => j === i ? { ...eq, wasUsed: !eq.wasUsed } : eq))
                          }
                          aria-pressed={item.wasUsed}
                          aria-label={`${item.name || 'Equipment'} used on this ticket`}
                          className="min-h-11 min-w-11 flex-shrink-0 flex items-center justify-center -my-1.5 -ml-1.5"
                        >
                          {/* The pill stays 20x36; the BUTTON around it is 44x44.
                              Gloved hands on a rig floor — the visual size of a
                              switch and the size of the thing you have to hit
                              are not the same measurement. */}
                          <span
                            className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors ${item.wasUsed ? 'bg-green-500' : 'bg-fieldiq-border'}`}
                          >
                            <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${item.wasUsed ? 'translate-x-4' : 'translate-x-0'}`} />
                          </span>
                        </button>
                        {/* The name takes the rest of the first line beside the switch and
                            wraps, so the days stepper drops to the next line on a phone
                            instead of squeezing the name down to its first letter. From
                            `sm` up it shares the line with the stepper as before. */}
                        <div className="grow min-w-0 basis-[calc(100%-3.5rem)] sm:basis-0">
                          <p className="text-sm font-medium text-white break-words">{item.name}</p>
                          {item.type && <p className="text-xs text-fieldiq-muted">{item.type}</p>}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            disabled={!item.wasUsed || (item.daysMode === 'crew' && item.daysUsed <= 1)}
                            onClick={() =>
                              setEquipmentUsage(prev => prev.map((eq, j) => j === i ? { ...eq, ...stepDays(eq, -1) } : eq))
                            }
                            className="w-11 h-11 flex items-center justify-center rounded-lg border border-fieldiq-border text-fieldiq-text text-base font-bold hover:bg-fieldiq-border/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            −
                          </button>
                          <span className={`w-8 text-center text-sm font-semibold ${item.wasUsed ? 'text-white' : 'text-fieldiq-muted'}`}>
                            {/* "auto" until the crew chooses: the API then takes the ticket's
                                days worked (a new row) or keeps what is stored. */}
                            {item.daysMode === 'auto' ? 'auto' : item.daysUsed}
                          </span>
                          <button
                            type="button"
                            disabled={!item.wasUsed}
                            onClick={() =>
                              setEquipmentUsage(prev => prev.map((eq, j) => j === i ? { ...eq, ...stepDays(eq, 1) } : eq))
                            }
                            className="w-11 h-11 flex items-center justify-center rounded-lg border border-fieldiq-border text-fieldiq-text text-base font-bold hover:bg-fieldiq-border/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            +
                          </button>
                          <span className={`text-xs ml-1 ${item.wasUsed ? 'text-fieldiq-muted' : 'text-fieldiq-muted/50'}`}>
                            {item.daysMode === 'auto' ? 'days' : item.daysUsed === 1 ? 'day' : 'days'}
                          </span>
                          {item.wasUsed && item.daysMode === 'crew' && (
                            <button
                              type="button"
                              aria-label={`${item.name || 'Equipment'} days back to auto`}
                              onClick={() =>
                                setEquipmentUsage(prev => prev.map((eq, j) => j === i ? { ...eq, daysMode: 'auto' } : eq))
                              }
                              className="text-[11px] text-fieldiq-accent hover:underline ml-1"
                            >
                              auto
                            </button>
                          )}
                        </div>
                        {item.wasUsed && (
                          <EquipmentHoursRuns
                            item={item}
                            ticketHours={formatTicketHours(editingTicket?.total_hours ?? editingTicket?.totalHours)}
                            onChange={(patch) =>
                              setEquipmentUsage(prev => prev.map((eq, j) => j === i ? { ...eq, ...patch } : eq))
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Line Items */}
              <div className="bg-fieldiq-dark rounded-xl border border-fieldiq-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-y-1 mb-3">
                  <h3 className="text-sm font-bold flex items-center gap-2">
                    <DollarSign size={16} className="text-fieldiq-accent" />
                    Line Items ({lineItems.length})
                  </h3>
                  <div className="flex items-center gap-3">
                    <AddDiscountButton lines={shownLines} onAdd={addDiscount} />
                    <button onClick={addLineItem} className="text-xs flex items-center gap-1 text-fieldiq-accent">
                      <Plus size={14} /> Add Item
                    </button>
                  </div>
                </div>

                {lineItems.length === 0 ? (
                  <div className="text-center py-6 text-fieldiq-muted text-sm">
                    <Info size={20} className="mx-auto mb-2" />
                    <p>No line items yet.</p>
                    <p className="text-xs mt-1">
                      Tap <strong>AI Suggest</strong> to auto-generate from rate sheets, or <strong>Add Item</strong> and search the price book.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {shownLines.map((item, i) => isDiscountLine(item) ? (
                      <DiscountLineRow
                        key={i}
                        line={item}
                        index={i}
                        lines={shownLines}
                        surface="card"
                        reasons={lineItemErrors[i]}
                        onEdit={edit => editDiscount(i, edit)}
                        onRemove={() => removeLineItem(i)}
                        rowRef={el => { lineItemRowRefs.current[i] = el; }}
                      />
                    ) : (
                      <div
                        key={i}
                        data-testid="line-item-row"
                        ref={el => { lineItemRowRefs.current[i] = el; }}
                        className={`bg-fieldiq-card rounded-xl p-3 space-y-2 ${rowErrorRingClass(!!lineItemErrors[i])}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 space-y-1">
                            <div>
                              <label className="text-[10px] text-fieldiq-muted">Description</label>
                              <RateSheetItemPicker
                                value={item.description ?? ''}
                                onChange={v => updateLineItem(i, 'description', v)}
                                onSelect={picked => pickRateSheetItem(i, picked)}
                                items={rateSheetItems ?? []}
                                state={rateItemsState}
                                preferredSheetId={jobRateSheetId}
                                surface="dark"
                                // Scoped to the reasons that render under THIS box. A refusal about
                                // the rate used to redden the description too, which sends the
                                // reader to a box that has nothing wrong with it.
                                invalid={splitRowReasons(lineItemErrors[i]).other.length > 0}
                                ariaLabel="Description"
                                placeholder={item.lineItemType === 'other' ? 'Describe this charge\u2026' : 'Search the price book, or type your own'}
                                linkState={priceBookLinkState(item)}
                                onOffBookChange={v => setLineOffBook(i, v)}
                                onAutoLink={match => autoLinkLine(i, match)}
                                rowUnit={item.unit}
                              />
                              {/*
                                The reasons that are NOT about the two money
                                boxes. Those render beside the boxes themselves
                                further down, so the words sit under the input
                                they name. See splitRowReasons for why this half
                                is the complement rather than an allow-list.
                              */}
                              {splitRowReasons(lineItemErrors[i]).other.length > 0 && (
                                <RowError reasons={splitRowReasons(lineItemErrors[i]).other} />
                              )}
                              {lineItemUnitNotes[i] && (
                                <p className="text-[11px] text-amber-400 mt-0.5">{lineItemUnitNotes[i]}</p>
                              )}
                            </div>
                            <div>
                              <label className="text-[10px] text-fieldiq-muted">Type</label>
                              <select
                                aria-label="Type"
                                value={item.lineItemType}
                                onChange={e => updateLineItem(i, 'lineItemType', e.target.value)}
                                className={`w-full bg-fieldiq-dark rounded px-2 py-1 text-xs border outline-none ${lineItemTypeErrors[i] ? 'border-red-500/60 text-red-400' : 'border-fieldiq-border'}`}
                              >
                                <option value="">Select type…</option>
                                {visibleLineItemTypeOptions(hiddenLineItemTypes, item.lineItemType).map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              {lineItemTypeErrors[i] && (
                                <p className="text-[11px] text-red-400 mt-0.5">{lineItemTypeErrors[i]}</p>
                              )}
                            </div>
                            {item.revenueCode && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-fieldiq-muted">Code: {item.revenueCode}</span>
                              </div>
                            )}
                          </div>
                          {/*
                            * Named, because an icon-only button has no
                            * accessible name at all — nothing for a screen
                            * reader to announce, and nothing for a test to
                            * address except by traversing the DOM to it.
                            */}
                          <button
                            aria-label={`Remove line ${i + 1}`}
                            onClick={() => removeLineItem(i)}
                            className="p-1 text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <label className="text-[10px] text-fieldiq-muted">Unit</label>
                            <select
                              aria-label="Unit"
                              value={item.unit ?? ''}
                              onChange={e => updateLineItem(i, 'unit', e.target.value || null)}
                              className="w-full bg-fieldiq-dark rounded px-2 py-1 text-xs border border-fieldiq-border outline-none"
                            >
                              <option value="">Select...</option>
                              {UNIT_OPTIONS.map(u => (
                                <option key={u.value} value={u.value}>{u.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] text-fieldiq-muted">Rate</label>
                            <input
                              type="number"
                              aria-label="Rate"
                              step="0.01"
                              onWheel={blurOnWheel}
                              value={item.rate ?? ''}
                              onChange={e => updateLineItem(i, 'rate', e.target.value === '' ? null : parseFloat(e.target.value))}
                              className={`w-full bg-fieldiq-dark rounded px-2 py-1 text-xs border outline-none ${fieldErrorClass(lineItemErrors[i], 'rate')}`}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-fieldiq-muted">Qty</label>
                            <input
                              type="number"
                              aria-label="Qty"
                              step="0.01"
                              onWheel={blurOnWheel}
                              value={item.quantity ?? ''}
                              onChange={e => updateLineItem(i, 'quantity', e.target.value === '' ? null : parseFloat(e.target.value))}
                              className={`w-full bg-fieldiq-dark rounded px-2 py-1 text-xs border outline-none ${fieldErrorClass(lineItemErrors[i], 'quantity')}`}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-fieldiq-muted">Total</label>
                            <div className="bg-fieldiq-dark rounded px-2 py-1 text-xs border border-fieldiq-border text-green-400 font-medium">
                              {fmtCurrency(item.total)}
                            </div>
                          </div>
                        </div>
                        {/*
                          Under the four boxes rather than under one of them:
                          the sentence is about the pair, and a column this
                          narrow would wrap it to five lines.
                        */}
                        {splitRowReasons(lineItemErrors[i]).money.length > 0 && (
                          <RowError reasons={splitRowReasons(lineItemErrors[i]).money} />
                        )}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                          {([1, 2, 3, 4] as const).map(slot => {
                            const heldId = item[`assignedTech${slot}Id` as const];
                            const slotLists = pickerListsForSlot(crewLists, showEveryone, heldId);
                            const orphan = orphanSlotOption(slotLists, heldId, item[`assignedTech${slot}Initials` as const]);
                            return (
                              <div key={slot}>
                                <label className="text-[10px] text-fieldiq-muted">Tech {slot}</label>
                                <select
                                  aria-label={`Tech ${slot}`}
                                  value={heldId ?? ''}
                                  onChange={e => setLineItemTech(i, slot, e.target.value || null)}
                                  className="w-full bg-fieldiq-dark rounded px-2 py-1 text-xs border border-fieldiq-border outline-none"
                                >
                                  <option value="">None</option>
                                  {slotLists.crew.map((c: any) => (
                                    <option key={crewId(c)} value={crewId(c)}>{crewLabel(c)}</option>
                                  ))}
                                  {slotLists.others.length > 0 && (
                                    <optgroup label="Not on this job">
                                      {slotLists.others.map((c: any) => (
                                        <option key={crewId(c)} value={crewId(c)}>{crewLabel(c)}</option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {orphan ? <option value={crewId(orphan)}>{crewLabel(orphan)}</option> : null}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                        {slotPrefill[i] === 'prefilled' && (
                          <p className="text-[11px] text-fieldiq-muted">Pre-filled from job positions — saving confirms it.</p>
                        )}
                        {isOnline && (
                          <div>
                            <button
                              type="button"
                              onClick={() => setShowEveryone(v => !v)}
                              className="text-[10px] text-fieldiq-accent"
                            >
                              {showEveryone ? 'Job crew only' : 'Not on this job? Show everyone'}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Subtotal */}
                    <div className="flex items-center justify-between pt-2 border-t border-fieldiq-border">
                      <span className="text-sm font-bold">Subtotal</span>
                      <span data-testid="editor-subtotal" className="text-lg font-bold text-green-400">
                        {fmtCurrency(subtotal, currency)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <PhotoUploader
                entityType="ticket"
                entityId={editingTicket.id}
                value={editingTicket.photo_urls ?? []}
                onChange={(photos) => setEditingTicket({ ...editingTicket, photo_urls: photos })}
                label="Job Photos"
                variant="grid"
              />

              {/*
                * Which of the two is spinning.
                *
                * Save Draft and Review are one write through one
                * `updateMutation`, so `isPending` alone cannot say which
                * button the hand pressed — showing the spinner on both would
                * be a guess, and the wrong half of it is a lie. React Query
                * hands back the vars the pending mutation was fired with, and
                * `review` is already the flag that separates the two outcomes
                * (see updateMutation's onSuccess), so it separates the two
                * indicators too. No second piece of state to fall out of step.
                */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    if (!validateLineItems() || !validateStringMetres() || !validateEquipmentUsage()) return;
                    const body = buildTicketBody();
                    updateMutation.mutate({ id: editingTicket.id, version: editingTicket.version, data: body });
                  }}
                  disabled={updateMutation.isPending}
                  className="flex-1 bg-fieldiq-card text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 border border-fieldiq-border disabled:opacity-50"
                >
                  {savingDraft ? (
                    <><Loader size={16} className="animate-spin" /> Saving...</>
                  ) : (
                    'Save Draft'
                  )}
                </button>
                {/*
                  * Review, not Submit. The submit call now fires from exactly
                  * one place — the confirmation dialog on the review step —
                  * so a ticket cannot reach the office without the hand having
                  * seen the office's own numbers for it.
                  *
                  * It saves first, through the same PUT Save Draft uses,
                  * because the totals it is about to show are computed by the
                  * server from the committed line items. There is no way to
                  * review real money without writing first, and working it out
                  * on the device instead would be a second answer to a
                  * question the API already answers.
                  */}
                <button
                  onClick={() => {
                    if (!validateLineItems() || !validateStringMetres() || !validateEquipmentUsage()) return;
                    updateMutation.mutate({
                      id: editingTicket.id,
                      version: editingTicket.version,
                      data: buildTicketBody(),
                      review: true,
                    });
                  }}
                  disabled={updateMutation.isPending}
                  className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-semibold py-3 rounded-xl flex items-center justify-center gap-2 min-h-[52px] disabled:opacity-50"
                >
                  {savingForReview ? (
                    <><Loader size={16} className="animate-spin" /> Saving...</>
                  ) : (
                    <><ClipboardCheck size={16} /> Review</>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-fieldiq-card rounded-xl p-4 space-y-3 text-sm">
              {editingTicket.notes && (
                <div>
                  <span className="text-fieldiq-muted block text-xs mb-1">Internal Notes</span>
                  <p className="text-white">{editingTicket.notes}</p>
                </div>
              )}
              {editingTicket.customer_notes && (
                <div>
                  <span className="text-fieldiq-muted block text-xs mb-1">Customer Notes</span>
                  <p className="text-white">{editingTicket.customer_notes}</p>
                </div>
              )}
              {editingTicket.actual_metres_run && (
                <div>
                  <span className="text-fieldiq-muted block text-xs mb-1">Metres Run</span>
                  <p className="text-white">{editingTicket.actual_metres_run}m</p>
                </div>
              )}
              {lineItems.length > 0 && (
                <div>
                  <span className="text-fieldiq-muted block text-xs mb-2">Line Items</span>
                  <div className="space-y-1.5">
                    {shownLines.map((li, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <span className="text-white truncate">{li.description || getLineItemTypeLabel(li.lineItemType)}</span>
                        <span className="text-green-400 font-medium flex-shrink-0">
                          {fmtLineMoney(li.total)}
                          {isDiscountLine(li) && isDiscountEstimate(li, shownLines) ? ' (estimate)' : ''}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-1.5 border-t border-fieldiq-border">
                      <span className="font-bold text-white">Subtotal</span>
                      <span className="font-bold text-green-400">{fmtCurrency(subtotal, currency)}</span>
                    </div>
                  </div>
                </div>
              )}
              {Array.isArray(editingTicket.photo_urls) && editingTicket.photo_urls.length > 0 && (
                <div>
                  <span className="text-fieldiq-muted block text-xs mb-2">Photos</span>
                  <div className="grid grid-cols-3 gap-2">
                    {editingTicket.photo_urls.map((photo: any, idx: number) => (
                      <img
                        key={photo.key ?? idx}
                        src={photo.url ?? photo}
                        alt={photo.label ?? `Photo ${idx + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              )}
              {!editingTicket.notes && !editingTicket.actual_metres_run && lineItems.length === 0 && (
                <p className="text-fieldiq-muted text-center py-4">No additional details on this ticket.</p>
              )}
            </div>
          )}
        </div>
        <ConfirmModal
          isOpen={aiReplaceConfirm && !!aiReplaceCopy}
          title={aiReplaceCopy?.title ?? ''}
          body={aiReplaceCopy?.body ?? ''}
          confirmLabel={aiReplaceCopy?.confirmLabel ?? ''}
          confirmVariant="destructive"
          onCancel={() => setAiReplaceConfirm(false)}
          onConfirm={() => {
            setAiReplaceConfirm(false);
            aiMutation.mutate(editingTicket.id);
          }}
        />
        {/*
          * The office-approved warning, in the hand's terms and not the
          * mechanism's. What actually happens is that the ticket reverts to
          * field_approved and approval's side effects — cert-day counting among
          * them — are undone; none of that is his to reason about. What is his
          * to reason about is that the office has to look at it again because
          * of him.
          *
          * confirmVariant is "primary", not "destructive": editing an
          * office-approved ticket is a normal, supported correction (the API
          * built revertOfficeApprovalForEdit for exactly this), not something
          * to paint red and make him feel he is breaking a rule. The point is
          * to inform, not to deter.
          */}
        <ConfirmModal
          isOpen={editUnlockConfirm}
          title="Edit an approved ticket?"
          confirmLabel="Edit ticket"
          confirmVariant="primary"
          onCancel={() => setEditUnlockConfirm(false)}
          onConfirm={() => {
            setEditUnlockConfirm(false);
            setEditUnlockedFor(editingTicket.id);
          }}
          body={
            <p>
              The office has already approved this ticket. Editing sends it back
              to them for re-approval.
            </p>
          }
        />
      </div>
    );
  }

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">My Tickets</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4">
        {message && (
          <div ref={listMessageRef} data-testid="list-message" className={`rounded-xl p-3 mb-4 flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-500/20 text-green-400'
            : message.type === 'warning' ? 'bg-amber-500/20 text-amber-400'
            : 'bg-red-500/20 text-red-400'}`}>
            {message.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
            <span className="text-sm flex-1">{message.text}</span>
            {message.sticky && (
              <button type="button" onClick={() => setMessage(null)} aria-label="Dismiss" className="shrink-0 px-1 font-bold hover:opacity-70">
                &times;
              </button>
            )}
          </div>
        )}

        {/* Status Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {FIELD_TICKET_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                activeTab === tab.key
                  ? 'bg-fieldiq-accent text-fieldiq-dark'
                  : 'bg-fieldiq-card text-fieldiq-text'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tickets List */}
        {isLoading ? (
          <div className="text-center py-12 text-fieldiq-text">Loading tickets...</div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 text-fieldiq-text">
            <FileText size={32} className="mx-auto mb-3 text-fieldiq-muted" />
            <p>No tickets found</p>
            <p className="text-xs mt-2">Create a ticket from a job to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket: any) => (
              <div
                key={ticket.id}
                className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden"
              >
                <button
                  onClick={() => {
                    if (fieldTicketWriteRefused(ticket)) {
                      // Do not open an editor that cannot save. Before this the
                      // row opened for every ticket in the list — and the list
                      // is the WIDE visibility rule — so a hand could fill in
                      // another hand's ticket and only find out on save, from a
                      // 404 reading "Ticket not found or not yours" about a
                      // ticket he was looking at.
                      //
                      // fieldTicketWriteRefused, NOT !canWriteFieldTicket: a
                      // row here carries no job crew, so the local rule cannot
                      // evaluate the designation clause and would refuse a
                      // DESIGNATED WRITER on a ticket someone else authored.
                      // This blocks only on a definite server `can_write:
                      // false`; anything unknown opens and the API answers.
                      // See lib/field-ticket-write.ts for why that is the
                      // recoverable direction.
                      setMessage({
                        type: 'error',
                        text: ticketWriteRefusalReason(ticket, user?.id, null)
                          ?? 'This ticket is not yours to edit.',
                      });
                      setTimeout(clearTransientMessage, 6000);
                      return;
                    }
                    setEditingTicket(ticket);
                  }}
                  className="w-full p-4 text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      {statusIcon(ticket.status)}
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm text-white">
                          {ticket.ticket_number || ticket.ticketNumber || 'Ticket'}
                          {ticket._pendingSync && <span className="ml-1.5 text-[10px] text-orange-400">(offline)</span>}
                        </h3>
                        <p className="text-xs text-fieldiq-muted truncate">
                          {ticket.customer_name || ticket.job_number || (readTicketDate(ticket) ? formatDate(readTicketDate(ticket)) : 'No date')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(ticket.status)}`}>
                        {statusLabel(ticket.status)}
                      </span>
                      <ChevronRight size={16} className="text-fieldiq-muted" />
                    </div>
                  </div>

                  {/* Extra info row */}
                  <div className="flex items-center gap-4 mt-2 text-xs text-fieldiq-muted">
                    {/* The API's ticket date (api#1329): the finish day when
                        the company dates tickets by it, else work_date. Read,
                        never derived — see lib/ticket-date.ts. Arrival and
                        finish both stay visible: the time span names whichever
                        day the calendar chip does not. */}
                    {readTicketDate(ticket) && (
                      <span className="flex items-center gap-1">
                        <Calendar size={11} /> {formatDate(readTicketDate(ticket))}
                      </span>
                    )}
                    {ticket.start_time && ticket.end_time && (
                      <span className="flex items-center gap-1">
                        <Clock size={11} /> {formatTime(ticket.start_time)} – {formatTime(ticket.end_time)}
                        {ticket.end_date && calendarDayOrRaw(ticket.end_date) !== calendarDayOrRaw(ticket.work_date) && (
                          calendarDayOrRaw(readTicketDate(ticket)) === calendarDayOrRaw(ticket.end_date)
                            ? <span className="text-fieldiq-accent"> (arrived {formatDate(ticket.work_date)})</span>
                            : <span className="text-fieldiq-accent"> (+{formatDate(ticket.end_date)})</span>
                        )}
                      </span>
                    )}
                    {/* Server-computed hours. Null (e.g. contradictory times)
                        shows as a warning, never as 0. */}
                    {formatTicketHours(ticket.total_hours) ? (
                      <span className="flex items-center gap-1">
                        {formatTicketHours(ticket.total_hours)} h
                      </span>
                    ) : hasContradictoryTimes(ticket.work_date, ticket.end_date, ticket.start_time, ticket.end_time) ? (
                      <span className="flex items-center gap-1 text-red-400">
                        <AlertTriangle size={11} /> Times contradict
                      </span>
                    ) : null}
                    {ticket.lsd && (
                      <span className="flex items-center gap-1">
                        <MapPin size={11} /> {ticket.lsd}
                      </span>
                    )}
                  </div>
                </button>

                {/* Delete bar for drafts */}
                {isDeletable(ticket.status) && (
                  <div className="border-t border-fieldiq-border px-4 py-2 flex justify-end">
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete ${ticket.ticket_number || 'this ticket'}?`)) {
                          deleteMutation.mutate(ticket.id);
                        }
                      }}
                      className="text-xs text-red-400 flex items-center gap-1 hover:text-red-300 transition-colors"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
